import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { Archive, ArchiveRestore, ListOrdered, MonitorPlay, Pencil, Plus, RefreshCw, Save, ShieldAlert, Trash2 } from 'lucide-react'
import PostHoverPreview, { useHoverPreviewAnchor } from '../components/PostHoverPreview'
import PostInspectorDrawer from '../components/PostInspectorDrawer'
import PostRow, { PostRowHeader } from '../components/PostRow'
import { moderationEditNotice, moderationHoldBadge, moderationPublishNotice, moderationRowsPollInterval } from '../components/moderation/moderationHoldStatus'
import ModerationOverrideDialog from '../components/moderation/ModerationOverrideDialog'
import { membersModerationBadge } from '../components/moderation/postGroupModerationModel'
import { BulkActionBar, FilterBar } from '../components/patterns'
import {
  Alert,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  HelpTip,
  IconButton,
  Input,
  LinkButton,
  PageHeader,
  PageLayout,
  Pagination,
  Select,
  SegmentedControl,
  StatePanel,
  Toggle,
} from '../components/common'
import { useOrganization } from '../contexts/OrganizationContext'
import { ARCHIVED_POSTS_SECTION, FEED_LIBRARY_EYEBROW, FEED_LIBRARY_SECTION_LABEL } from '../config/studioRoutes'
import { PRODUCT_KEYS } from '../config/studioProducts'
import { ROLES } from '../constants/roles'
import { commandCenterApi, feedOpsApi, kambiApi, organizationApi, parlayApi, postApi, surfacesApi, tagApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { useFeedOpsOrderMutations } from '../hooks/useFeedOpsOrderMutations'
import { batchTargetsByOrg, mapWithConcurrency, runBatches, runInChunks } from '../utils/batchRequests'
import { bookRegulationLabels, formatRegulationName, organizationRegulationMappings } from '../utils/regulationLabels'
import { resolveVisibleItemLimit } from '@feed-widget/surfaces/visibleItemLimit'
import styles from './FeedOpsPage.module.css'
import MediaSectionTabs from '../components/MediaSectionTabs'


const INLINE_INSPECTOR_QUERY = '(min-width: 75rem)'

function useInlineInspector() {
  const [inline, setInline] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia?.(INLINE_INSPECTOR_QUERY).matches
  ))

  useEffect(() => {
    const media = window.matchMedia?.(INLINE_INSPECTOR_QUERY)
    if (!media) return undefined
    const update = () => setInline(media.matches)
    update()
    media.addEventListener?.('change', update)
    return () => media.removeEventListener?.('change', update)
  }, [])

  return inline
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'approved', label: 'Approved' },
  { value: 'pending_approval', label: 'Pending approval' },
  { value: 'draft', label: 'Draft' },
  { value: 'rejected', label: 'Rejected' },
]

const VISIBILITY_OPTIONS = [
  { value: '', label: 'All visibility' },
  { value: 'visible', label: 'Visible only' },
  { value: 'hidden', label: 'Hidden only' },
  { value: 'pinned', label: 'Pinned only' },
]

// 'live' asks the server for the intersection of Feed API deliverability on
// the selected surface and the canonical 'active' operator lifecycle, so the
// browse shows what a book currently looks like. The value key stays 'live'
// because it is the omitted URL default and is deep-linked as book_state=all
// elsewhere; only the operator-facing label changed.
const BOOK_STATE_OPTIONS = [
  { value: 'live', label: 'Active posts' },
  { value: 'all', label: 'All posts' },
]

// Central Manage Posts' primary lifecycle control. Active is the default
// browse view; Archived and All posts are explicit opt-ins, each persisted in
// the URL and backed by server-computed lifecycle_counts so the tabs never
// require a client-side round trip to know their own counts.
const LIFECYCLE_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'pending_delete', label: 'Pending deletion' },
  { value: 'all', label: 'All posts' },
]

const SOURCE_OPTIONS = [
  { value: 'creator', label: 'Creator posts' },
  { value: 'all', label: 'All sources' },
  { value: 'creator_winning_ticket', label: 'Creator wins' },
  { value: 'winning_ticket', label: 'Winning tickets' },
  { value: 'personalized_ticket', label: 'Ticket posts' },
  { value: 'personalized_recommendation', label: 'Crowd recommendations' },
  { value: 'personalized', label: 'Personalized posts' },
  { value: 'promotion', label: 'Promotions' },
  { value: 'image', label: 'Image posts' },
  { value: 'video', label: 'Video posts' },
]

const CONTENT_TYPE_OPTIONS = [
  { value: '', label: 'All content' },
  { value: 'standard', label: 'Standard posts' },
  { value: 'parlay', label: 'Parlays' },
]

const PARLAY_STATUS_OPTIONS = [
  { value: '', label: 'All parlay statuses' },
  { value: 'ready', label: 'Ready' },
  { value: 'needs_refresh', label: 'Needs refresh' },
  { value: 'not_available', label: 'Not available' },
]

const PARLAY_ORIGIN_OPTIONS = [
  { value: '', label: 'All parlay origins' },
  { value: 'operator', label: 'Operator-authored' },
  { value: 'generated', label: 'Generated' },
]

// Studio provisions every organization's primary feed surface with the
// canonical `default-feed` slug and the carousel surface with `widget`.
// Command Center is cross-organization, so it names these real per-book
// slugs directly: buildFeedOpsQueuePredicates joins feed_surfaces on
// `fs.slug = surface`, and a slug that exists in no book matches nothing.
const DEFAULT_SURFACE_SLUG = 'default-feed'
const COMMAND_CENTER_SURFACE_OPTIONS = [
  { value: DEFAULT_SURFACE_SLUG, label: 'Feed' },
  { value: 'widget', label: 'Widget' },
]

const QUEUE_PAGE_SIZE = 50
const OVERVIEW_ORDER_FETCH_SIZE = 1000

const PLACEMENT_STRATEGY_LABELS = {
  top: 'Top placement',
  middle: 'Middle placement',
  bottom: 'Bottom placement',
  random: 'Randomized placement',
}

const BOOK_STATE_VALUES = new Set(BOOK_STATE_OPTIONS.map((option) => option.value))
const LIFECYCLE_VALUES = new Set(LIFECYCLE_OPTIONS.map((option) => option.value))
const SOURCE_VALUES = new Set(SOURCE_OPTIONS.map((option) => option.value))
const STATUS_VALUES = new Set(STATUS_OPTIONS.map((option) => option.value))
const VISIBILITY_VALUES = new Set(VISIBILITY_OPTIONS.map((option) => option.value))
const CONTENT_TYPE_VALUES = new Set(CONTENT_TYPE_OPTIONS.map((option) => option.value))
const PARLAY_STATUS_VALUES = new Set(PARLAY_STATUS_OPTIONS.map((option) => option.value))
const PARLAY_ORIGIN_VALUES = new Set(PARLAY_ORIGIN_OPTIONS.map((option) => option.value))
const STATUS_ALIASES = {
  active: 'approved',
  live: 'approved',
  published: 'approved',
  pending: 'pending_approval',
  drafts: 'draft',
  all: '',
}
const VISIBILITY_ALIASES = {
  archived: 'hidden',
  all: '',
}
const GENERATED_SOURCE_VALUES = new Set([
  'creator_winning_ticket',
  'winning_ticket',
  'personalized_ticket',
  'personalized_recommendation',
  'personalized',
])
// Generated posts are an internal-only view, like the Generated posts page:
// operators still see live generated posts under "All sources", but get no
// generated-only source filters (the API refuses them below internal admin).
const OPERATOR_SOURCE_OPTIONS = SOURCE_OPTIONS.filter((option) => !GENERATED_SOURCE_VALUES.has(option.value))
const OPERATOR_SOURCE_VALUES = new Set(OPERATOR_SOURCE_OPTIONS.map((option) => option.value))
const LEGACY_TAB_FILTERS = {
  active: { status: 'approved' },
  live: { status: 'approved' },
  published: { status: 'approved' },
  pending: { status: 'pending_approval' },
  pending_approval: { status: 'pending_approval' },
  draft: { status: 'draft' },
  drafts: { status: 'draft' },
  rejected: { status: 'rejected' },
  archived: { visibility: 'hidden' },
  hidden: { visibility: 'hidden' },
}

function normalizedFilter(value, aliases, allowedValues) {
  if (value === null || value === '') return null
  const normalized = aliases[value] ?? value
  return allowedValues.has(normalized) ? normalized : null
}

function normalizedScope(value, aliases, allowedValues, defaultValue) {
  if (value === null || value === '') return defaultValue
  const normalized = aliases[value] ?? value
  return allowedValues.has(normalized) ? normalized : defaultValue
}

function isGeneratedParlay(item) {
  return item?.parlay?.origin === 'generated'
}


function isGeneratedQueueItem(item) {
  return GENERATED_SOURCE_VALUES.has(item.content_origin) || isGeneratedParlay(item)
}

// The Command Center queue returns one row per logical post with
// group_members carrying every physical post it collapsed (distributed-post
// members and forked regulation variants). Per-org dashboard rows have no
// members, so a row stands in for itself.
function rowMembers(item) {
  if (item.group_members?.length) return item.group_members
  return [{
    post_id: item.id,
    organization_id: item.organization_id,
    organization_slug: item.organization_slug,
    organization_name: item.organization_name,
    provider_id: item.provider_id,
    target_offerings: item.target_offerings || [],
    is_hidden: item.is_hidden,
    lifecycle: item.lifecycle,
    post_type: item.post_type,
    content_origin: item.content_origin,
    delete_capability: item.delete_capability,
    moderation: item.moderation,
  }]
}

function memberIsGenerated(member) {
  return GENERATED_SOURCE_VALUES.has(member.content_origin)
}

// Per-member eligibility for each Command Center bulk action. Targets are
// validated all-or-nothing server-side, so an ineligible member must never
// reach the envelope: it is filtered here and the row simply contributes
// fewer targets.
const BULK_MEMBER_FILTERS = {
  archive: (member) => member.lifecycle === 'active' && !member.is_hidden,
  restore: (member) => member.is_hidden && member.lifecycle === 'archived_manual' && !memberIsGenerated(member),
  // With an exact regulation selected, "restore" clears a per-regulation
  // visibility override on a post that is itself still active.
  offeringRestore: (member) => member.is_hidden && member.lifecycle === 'active' && !memberIsGenerated(member),
  moveToTop: (member) => !memberIsGenerated(member),
  // A saved drag order writes a plain manual rank, which generated and
  // personalized content never carries; the server rejects it too.
  setOrder: (member) => !memberIsGenerated(member),
  deleteDrafts: (member) => Boolean(member.delete_capability?.allowed),
  promote: (member) => member.lifecycle === 'active' && !memberIsGenerated(member) && member.post_type !== 'promotion',
  demote: (member) => member.lifecycle === 'active' && !memberIsGenerated(member) && member.post_type === 'promotion',
}

// How many distributed posts a bulk archive/restore changes at once. Each is
// its own server transaction, lock and revision, so they need not queue.
const GROUP_LIFECYCLE_CONCURRENCY = 3

// The All Regulations overview lists families: one logical post in this book,
// made of every physical post (root, regulation forks, per-regulation source
// imports) the server summarizes in family.members. Archive, restore and
// delete act on those member posts only, so other books are never touched.
// A family without members (older API) offers no actions: fail closed.
function familyMembers(family) {
  return family?.members || []
}

// A member marked for deletion (#326) is never archived, restored or marked
// again from the overview (the server refuses with pending_delete): it only
// offers Undo deletion / Erase now, and only for standalone posts, since a
// distributed post's pending members are undone together in Command Center.
function memberIsPendingDelete(member) {
  return member.lifecycle === 'pending_delete' || Boolean(member.pending_delete_at)
}

const FAMILY_MEMBER_FILTERS = {
  archive: (member) => !member.is_hidden && !memberIsPendingDelete(member),
  // Raw is_hidden, like the queue: an archived post that still presents as
  // pending_approval/rejected (postlifecycle.Present precedence) must stay restorable.
  restore: (member) => member.is_hidden && member.lifecycle !== 'archived_settled' && !memberIsPendingDelete(member) && !memberIsGenerated(member),
  delete: (member) => Boolean(member.delete_capability?.allowed) && !memberIsPendingDelete(member),
  undoDelete: (member) => memberIsPendingDelete(member) && !member.post_group_id,
  forceDelete: (member) => memberIsPendingDelete(member) && !member.post_group_id,
}

function familyMemberIds(family, action) {
  return familyMembers(family).filter(FAMILY_MEMBER_FILTERS[action]).map((member) => member.post_id)
}

function familyIsDistributed(family) {
  return Boolean(family?.representative?.post_group_id)
    || familyMembers(family).some((member) => member.post_group_id)
}

// A family is deletable when every member is, ignoring copies already marked
// for deletion: a mixed family marks its remaining members, a wholly pending
// one is not deletable (it offers Undo deletion / Erase now instead).
function familyIsDeletable(family) {
  const members = familyMembers(family)
  return members.length > 0
    && members.some(FAMILY_MEMBER_FILTERS.delete)
    && members.every((member) => memberIsPendingDelete(member) || FAMILY_MEMBER_FILTERS.delete(member))
}

function familyHasPendingDelete(family) {
  return familyMembers(family).some(memberIsPendingDelete)
}

function familyIsPendingDelete(family) {
  const members = familyMembers(family)
  return members.length > 0 && members.every(memberIsPendingDelete)
}

// The earliest erase deadline among a pending family's members (they may have
// been marked at different times), falling back to the representative's.
function familyPendingDeleteExpiresAt(family) {
  const deadlines = familyMembers(family)
    .map((member) => member.pending_delete_expires_at)
    .filter(Boolean)
    .sort((a, b) => Date.parse(a) - Date.parse(b))
  return deadlines[0] || family?.representative?.pending_delete_expires_at || null
}

function isFutureDate(value) {
  const timestamp = Date.parse(value)
  return Number.isFinite(timestamp) && timestamp > Date.now()
}

function readLegacyQueueFilters(searchParams, commandCenter, sourceValues = SOURCE_VALUES) {
  const tab = searchParams.get('tab') || ''
  const tabFilters = LEGACY_TAB_FILTERS[tab] || {}
  const status = normalizedFilter(searchParams.get('status'), STATUS_ALIASES, STATUS_VALUES)
  const visibility = normalizedFilter(searchParams.get('visibility'), VISIBILITY_ALIASES, VISIBILITY_VALUES)
  const requestedSurface = searchParams.get('surface') || DEFAULT_SURFACE_SLUG
  // Command Center URLs written before surfaces were keyed by real
  // feed_surfaces slugs used surface=feed; coerce that (and any other
  // unknown slug) to the canonical default surface.
  const surface = commandCenter && !COMMAND_CENTER_SURFACE_OPTIONS.some((option) => option.value === requestedSurface)
    ? DEFAULT_SURFACE_SLUG
    : requestedSurface
  const requestedContentType = searchParams.get('content_type') || ''
  const requestedParlayStatus = searchParams.get('parlay_status') || ''
  const requestedParlayOrigin = searchParams.get('parlay_origin') || ''
  const parlayStatus = PARLAY_STATUS_VALUES.has(requestedParlayStatus) ? requestedParlayStatus : ''
  const parlayOrigin = PARLAY_ORIGIN_VALUES.has(requestedParlayOrigin) ? requestedParlayOrigin : ''
  const contentType = CONTENT_TYPE_VALUES.has(requestedContentType) ? requestedContentType : ''
  // Lifecycle tabs and the personalized toggle are a Command Center-only
  // control; the per-org dashboard keeps its existing status/visibility
  // filters instead of a second, overlapping lifecycle concept.
  const requestedLifecycle = searchParams.get('lifecycle') || ''
  const lifecycle = commandCenter
    ? (LIFECYCLE_VALUES.has(requestedLifecycle) ? requestedLifecycle : 'active')
    : ''
  const includePersonalized = commandCenter && searchParams.get('include_personalized') === 'true'
  // Held by moderation is an organization filter; Command Center rows are
  // families spanning brands.
  const moderation = !commandCenter && searchParams.get('moderation') === 'held' ? 'held' : ''

  return {
    bookState: normalizedScope(searchParams.get('book_state'), {}, BOOK_STATE_VALUES, 'live'),
    source: normalizedScope(searchParams.get('source'), {}, sourceValues, 'creator'),
    surface,
    search: searchParams.get('search') || searchParams.get('q') || '',
    creator: commandCenter ? '' : searchParams.get('creator') || '',
    offering: commandCenter ? '' : (searchParams.get('offering') || '').trim(),
    status: status ?? tabFilters.status ?? '',
    visibility: visibility ?? tabFilters.visibility ?? '',
    contentType: contentType || (parlayStatus || parlayOrigin ? 'parlay' : ''),
    parlayStatus,
    parlayOrigin,
    generationRunIds: (searchParams.get('generation_run_id') || '').trim(),
    lifecycle,
    includePersonalized,
    moderation,
  }
}

function formatDateTime(value) {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}

// The list column only needs the day; the full timestamp belongs in the
// inspector, where there is room for it.
function formatListDate(value) {
  if (!value) return null
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
// The erase deadline is the server-computed expiry (mark time + retention
// window), never the mark timestamp itself — rendering the mark time would
// always read as imminent erasure. If the server has not supplied an expiry,
// state the retention window instead of inventing a bogus deadline.
function pendingDeleteCountdown(expiresAt) {
  if (!expiresAt) return 'Erases after the retention window'
  const hours = Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 3600000))
  return hours > 0 ? `Erases in ~${hours}h` : 'Erases soon'
}

// Regulation naming is shared with the post composer so both surfaces label a
// regulation identically; see utils/regulationLabels.

function formatDateTimeInput(value) {
  if (!value) return ''
  const date = new Date(value)
  const pad = (part) => String(part).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function toIsoOrNull(value) {
  if (!value) return null
  return new Date(value).toISOString()
}

function parlayStateLabel(item, offeringLabel) {
  const placement = item.is_hidden
    ? (offeringLabel ? `Archived for ${offeringLabel}` : 'Archived')
    : item.override?.placement_strategy && item.override.placement_strategy !== 'manual'
      ? `${item.override.placement_strategy} placement`
      : item.override?.manual_rank > 0 ? 'Manual order' : 'Automatic rank'
  if (!item.parlay) return placement

  const lifecycle = item.is_published
    ? 'Published'
    : item.approval_status === 'pending_approval'
      ? 'Awaiting review'
      : item.approval_status === 'approved' ? 'Approved' : 'Draft'
  const readiness = item.parlay.evaluation_status === 'ready'
    ? `Provider valid until ${formatDateTime(item.parlay.valid_until)}`
    : item.parlay.evaluation_status === 'needs_refresh'
      ? 'Provider refresh required'
      : 'Provider unavailable'
  return `${lifecycle} · ${readiness} · ${placement}`
}

function overviewFamilyLabel(family) {
  return family?.representative?.title || family?.representative?.text || 'Post'
}

function familyRootId(family) {
  return family?.root_post_id || family?.representative?.id || null
}

// Regulation counts are what an operator reasons about; the per-record counts
// only matter as the secondary "and how do they differ" line.
function familyReach(family) {
  const count = family.regulation_count ?? 0
  const detail = []
  if (family.hidden_count > 0) detail.push(`${family.hidden_count} hidden`)
  if (family.boosted_count > 0) detail.push(`${family.boosted_count} boosted`)
  // A shared post is one post record that several regulations run as is.
  if (family.shared_post_count > 0) {
    detail.push(`${family.shared_post_count} ${family.shared_post_count === 1 ? 'post' : 'posts'} shared across regulations`)
  }
  return {
    label: count === 1 ? '1 regulation' : `${count} regulations`,
    detail: detail.join(' · '),
  }
}

// A regulation-scoped queue row is a one-member family, so the drawer can be
// the single edit surface in both list modes. Only the selected regulation's
// settings are known here, so only that row is listed.
function queueItemAsFamily(item, offeringId) {
  const override = item.override || {}
  const targets = item.target_offerings || []
  const offeringIds = offeringId ? [offeringId] : targets
  return {
    family_key: `post:${item.id}`,
    root_post_id: item.id,
    variant_count: 0,
    representative: {
      ...item,
      creator: item.creator_id
        ? { id: item.creator_id, display_name: item.creator_name || '', avatar_url: item.creator_avatar || '' }
        : null,
    },
    variants: offeringIds.map((id) => ({
      post_id: item.id,
      offering_id: id,
      is_variant: false,
      shared_post: targets.length > 1,
      target_offerings: targets,
      is_hidden: Boolean(item.is_hidden),
      boost_factor: override.boost_factor ?? item.manual_priority ?? 1,
      pin_to_top: Boolean(override.pin_to_top),
      manual_rank: override.manual_rank ?? null,
      placement_strategy: override.placement_strategy || '',
    })),
  }
}


function compareOrder(a, b) {
  return a.length !== b.length || a.some((item, index) => item.id !== b[index]?.id)
}

// The queue sorts every manually placed row above every automatically
// ordered one, so a saved rank is only truthful for a page whose first row
// is already manually placed: ranking an automatic page 2 would lift its
// rows above the untouched page 1. Only pinned rows and the 'manual'/'top'
// strategies sort in that leading block — a row placed 'middle', 'bottom'
// or 'random' carries a rank but sorts *below* the automatic ones, so an
// active override alone is not enough to make a later page rankable.
function hasManualPlacement(item) {
  const override = item?.override
  if (!override) return false
  return Boolean(
    override.pin_to_top ||
      (override.manual_rank > 0 && ['manual', 'top'].includes(override.placement_strategy)),
  )
}

// A grouped post is only editable through the Distributed post editor, which
// lives under /command-center behind `ProtectedRoute requireCommandCenterAccess`
// (App.jsx). Without that access there is no editor to send the operator to,
// so offering one would dead-end them at the auth wall and disclose that the
// post is distributed in the first place.
function canUsePostEditor(item, canOpenGroupEditor = false) {
  if (!item || isGeneratedParlay(item)) return false
  if (item.post_group_id) return canOpenGroupEditor
  return item.can_edit_directly !== false
    && (!item.content_origin || item.content_origin === 'creator')
}

const GENERATED_PARLAY_PUBLISH_REQUIRED_CODE = 'generated_parlay_publish_required'

// Operator-facing translations for POST /command-center/feed-ops/bulk-commands
// per-target rejection codes. The service's raw message strings are written
// for API consumers and some name implementation nouns ("post group",
// "offering") that operator copy must not use — see the book/regulation/
// Distributed post naming rule. A code without an entry here falls back to
// the service's own message, which is safe for the remaining codes.
const BULK_TARGET_REASON_COPY = {
  organization_not_found: 'Book not found.',
  provider_mismatch: 'Belongs to a different provider than the rest of this action.',
  post_not_found: 'Post not found.',
  offering_not_targeted: 'Not published to this regulation.',
  grouped_post: 'Part of a Distributed post — use the Distributed post editor for this change.',
  group_managed: 'Part of a Distributed post — archive it here or manage it through the Distributed post editor.',
  not_archivable: 'Not in an archivable state.',
  settlement_archived: 'Archived because its attached bet settled — this cannot be restored.',
  not_restorable: 'Not currently archived.',
  [GENERATED_PARLAY_PUBLISH_REQUIRED_CODE]: 'Generated parlays must be approved and published from the parlay workflow.',
  source_managed: 'Managed by a source feed — archive it instead.',
  generated: 'Generated posts are retained for source history — archive this post instead.',
  pending_delete: 'Pending deletion — undo the deletion first.',
  not_pending_delete: 'Not marked for deletion.',
  already_pending_delete: 'Already marked for deletion.',
  group_pending_delete: 'This distributed post is pending deletion.',
  not_manual_draft: 'Only manually created posts can be permanently deleted.',
  placement_unsupported: 'Generated or personalized content requires the source-feed workflow for placement.',
  rank_missing: 'No position was recorded for this post — refresh and try again.',
  surface_not_found: 'Surface not found for this book.',
  visible_item_limit_unsupported: 'This surface does not support a visible-item limit.',
  post_type_unsupported: 'Generated and personalized content cannot be marked as a promotion.',
  lifecycle_not_active: 'Only active posts can change here — restore the post first.',
}

const OVERVIEW_REORDER_WARNING = 'No regulation is selected. Saving this order applies it to every regulation these posts run in. Choose a regulation to order only that one.'

function bulkTargetReasonMessage(reason) {
  return BULK_TARGET_REASON_COPY[reason.code] || reason.message
}

// Expands selected rows into explicit (organization, post, surface,
// offering) bulk targets, one per collapsed family member. A blank
// regulation filter never means "all": when no regulation is selected,
// every regulation a member is known to serve is named explicitly, using
// the member's own target_offerings when it has one, or its book's full
// regulation catalog when the member carries no restriction of its own (an
// empty target_offerings list means "every regulation this book supports",
// mirroring feedOpsBulkOfferingTargeted on the server).
function resolveBulkTargetOfferings(member, offering, orgOfferingIds) {
  if (offering) return [offering]
  if (member.target_offerings?.length) return member.target_offerings
  return orgOfferingIds || []
}

// dedupeByPost collapses a member's regulation fan-out down to its first
// resolved regulation. Every fan-out target still names a real, targeted
// regulation (never "all"), but delete_drafts and set_post_type write the
// whole posts row once — unlike move_to_top/set_visibility/
// set_visible_item_limit, which are genuinely per-regulation writes and
// want the full fan-out so every regulation's live feed gets its own
// realtime removal. memberFilter keeps ineligible members out of the
// envelope entirely: server validation is all-or-nothing, so one bad
// target would reject every other target with it.
function buildBulkTargets(posts, { surface, offering, orgOfferingsById, dedupeByPost = false, memberFilter }) {
  const targets = []
  const seenPostIds = new Set()
  posts.forEach((item) => {
    rowMembers(item).forEach((member) => {
      if (memberFilter && !memberFilter(member)) return
      if (dedupeByPost && seenPostIds.has(member.post_id)) return
      const offerings = resolveBulkTargetOfferings(member, offering, orgOfferingsById.get(member.organization_id))
      const offeringsToUse = dedupeByPost ? offerings.slice(0, 1) : offerings
      offeringsToUse.forEach((offeringId) => {
        targets.push({
          organization_id: member.organization_id,
          post_id: member.post_id,
          surface,
          offering_id: offeringId,
        })
      })
      if (dedupeByPost) seenPostIds.add(member.post_id)
    })
  })
  return targets
}

// Builds the one `set_order` envelope a saved Command Center drag order
// sends. Every family on the page contributes its absolute queue position
// as manual_rank — including rows the operator never touched, so a later
// save can never reshuffle them against each other — and every member of a
// family shares that family's rank, mirroring the per-organization
// overview-order variant rule. Generated parlays and generated/personalized
// members are skipped entirely (they are not draggable and the server
// rejects them); the gap their skipped position leaves in the rank sequence
// is harmless, since only the relative order matters.
//
// With applyToAllStates the regulation is left blank, which makes
// resolveBulkTargetOfferings fan each member out to its own
// target_offerings — or its book's full regulation catalog when it carries
// none — exactly as every other bulk action does. A post that does not run
// in a regulation therefore never receives a rank there.
function buildCommandCenterOrderEnvelope({
  queueDraft, queueOffset, surface, offering, applyToAllStates, orgOfferingsById, providerId,
}) {
  const ranks = {}
  const rows = []
  queueDraft.forEach((item, index) => {
    if (isGeneratedParlay(item)) return
    const members = rowMembers(item).filter(BULK_MEMBER_FILTERS.setOrder)
    if (!members.length) return
    members.forEach((member) => {
      ranks[member.post_id] = queueOffset + index + 1
    })
    rows.push(item)
  })
  const targets = buildBulkTargets(rows, {
    surface,
    offering: applyToAllStates ? '' : offering,
    orgOfferingsById,
    memberFilter: BULK_MEMBER_FILTERS.setOrder,
  })
  if (!targets.length) return null
  return {
    idempotency_key: crypto.randomUUID(),
    provider_id: providerId,
    action: 'set_order',
    targets,
    parameters: { ranks },
  }
}

// Operator-facing scope text shown before a Command Center bulk action runs
// and on its result, in book/regulation language — never organization/
// jurisdiction/offering. Computed over the affected rows' members (envelope
// rows and whole distributed posts alike) so the summary covers both paths.
function bulkSelectionScopeSummary(posts, { offering, orgOfferingsById, memberFilter }) {
  const books = new Set()
  const regulations = new Set()
  posts.forEach((item) => {
    rowMembers(item).forEach((member) => {
      if (memberFilter && !memberFilter(member)) return
      books.add(member.organization_id)
      resolveBulkTargetOfferings(member, offering, orgOfferingsById.get(member.organization_id))
        .forEach((offeringId) => regulations.add(offeringId))
    })
  })
  const bookLabel = `${books.size} ${books.size === 1 ? 'book' : 'books'}`
  const regulationLabel = `${regulations.size} ${regulations.size === 1 ? 'regulation' : 'regulations'}`
  return `${bookLabel} · ${regulationLabel}`
}



function PageSelectionControl({
  loadedCount,
  selectedCount,
  allLoadedSelected,
  mutationPending,
  onSelectPage,
  onClearSelection,
}) {
  const checkboxRef = useRef(null)

  useEffect(() => {
    if (!checkboxRef.current) return
    checkboxRef.current.indeterminate = selectedCount > 0 && !allLoadedSelected
  }, [allLoadedSelected, selectedCount])

  return (
    <label className={styles.pageSelection}>
      <input
        ref={checkboxRef}
        type="checkbox"
        checked={allLoadedSelected}
        disabled={loadedCount === 0 || mutationPending}
        onChange={(event) => {
          if (event.target.checked) {
            onSelectPage()
          } else {
            onClearSelection()
          }
        }}
      />
      <span>{`Select page (${loadedCount})`}</span>
    </label>
  )
}

// Shows a Command Center bulk command's outcome in place — no navigation, no
// toast-only feedback that disappears before an operator can read it. A
// rejected command shows every failing target's reason (the transaction that
// produced it wrote nothing); a committed command shows how many posts it
// updated and the book/regulation scope it covered. A distributed-post
// action can partially complete (each group is its own atomic server-side
// transaction), so a result may carry both a success count and per-group
// failure reasons.
function BulkCommandResultPanel({ result, organizations, regulationLabelById, onDismiss }) {
  if (!result) return null

  const organizationName = (organizationId) => (
    organizations.find((organization) => organization.id === organizationId)?.name || organizationId
  )

  if (result.reasons?.length) {
    const partial = Boolean(result.resultCount)
    return (
      <Alert variant={partial ? 'warning' : 'error'} role="alert" className={styles.bulkResultPanel}>
        <div className={styles.bulkResultHeader}>
          <strong>{partial ? `${result.actionLabel} partially complete` : `${result.actionLabel} could not run`}</strong>
          <Button variant="ghost" size="compact" onClick={onDismiss}>Dismiss</Button>
        </div>
        <p className={styles.bulkResultScope}>
          {partial
            ? `Updated ${result.resultCount} ${result.resultCount === 1 ? 'post' : 'posts'} across ${result.scopeSummary}. The rest need attention:`
            : `Scope: ${result.scopeSummary}. No posts were changed.`}
        </p>
        <ul className={styles.bulkResultList}>
          {result.reasons.map((reason) => (
            <li key={`${reason.index}-${reason.code}-${reason.message}`}>
              {reason.target ? (
                <span className={styles.bulkResultTarget}>
                  {`${organizationName(reason.target.organization_id)} · ${regulationLabelById.get(reason.target.offering_id) || reason.target.offering_id}`}
                </span>
              ) : null}
              <span>{bulkTargetReasonMessage(reason)}</span>
            </li>
          ))}
        </ul>
      </Alert>
    )
  }

  return (
    <Alert variant="success" className={styles.bulkResultPanel}>
      <div className={styles.bulkResultHeader}>
        <strong>{result.successHeader || `${result.actionLabel} complete`}</strong>
        <Button variant="ghost" size="compact" onClick={onDismiss}>Dismiss</Button>
      </div>
      <p className={styles.bulkResultScope}>
        {result.successBody || `Updated ${result.resultCount} ${result.resultCount === 1 ? 'post' : 'posts'} across ${result.scopeSummary}.`}
      </p>
    </Alert>
  )
}


function QueueControls({
  commandCenter,
  archivedOnly,
  canSelectPosts,
  canBulkVisibility,
  canBulkDelete,
  providers,
  organizations,
  providerId,
  organizationId,
  setProviderId,
  setOrganizationId,
  surface,
  setSurface,
  surfaceOptions,
  offering,
  setOffering,
  offeringOptions,
  scopeLabel,
  bookState,
  setBookState,
  source,
  setSource,
  sourceOptions,
  moderationFilter,
  moderationHeldCount,
  onToggleModerationHeld,
  includePersonalized,
  setIncludePersonalized,
  status,
  setStatus,
  visibility,
  setVisibility,
  contentType,
  setContentType,
  parlayStatus,
  setParlayStatus,
  parlayOrigin,
  setParlayOrigin,
  creator,
  setCreator,
  tagId,
  setTagId,
  availableTags,
  resultRange,
  searchLoading,
  onRefresh,
  refreshPending,
  activeFilters,
  onRemoveFilter,
  onClearAdvancedFilters,
  selectedCount,
  selectedVisibleCount,
  selectedArchivedCount,
  deletableSelectedCount,
  protectedSelectedCount,
  deleteRestrictionText,
  onClearSelection,
  onRestoreSelected,
  onArchiveSelected,
  onDeleteSelected,
  visibilityActionPending,
  deletePending,
  canBulkCommand,
  bulkScopeSummary,
  bulkGroupedSelectedCount,
  bulkArchivableCount,
  bulkRestorableCount,
  bulkMoveToTopEligibleCount,
  bulkDeleteEligibleCount,
  bulkPromoteEligibleCount,
  bulkDemoteEligibleCount,
  bulkCommandPending,
  bulkCommandPendingKind,
  bulkVisibleItemLimitInput,
  setBulkVisibleItemLimitInput,
  onBulkArchive,
  onBulkRestore,
  onBulkMoveToTop,
  onBulkDeleteDrafts,
  onBulkPromote,
  onBulkDemote,
  onBulkSetVisibleItemLimit,
}) {
  const selectionMutationPending = Boolean(visibilityActionPending) || deletePending

  return (
    <>
      <section className={styles.scopeBar} aria-label="Post scope">
        <div className={styles.scopeControls}>
          {commandCenter ? (
            <>
              <FormField
                className={styles.scopeField}
                compact
                label="Provider"
                type="select"
                value={providerId}
                options={[
                  { value: '', label: 'All providers' },
                  ...providers.map((provider) => ({ value: provider.id, label: provider.name })),
                ]}
                onChange={(event) => {
                  setProviderId(event.target.value)
                  setOrganizationId('')
                  setOffering('')
                }}
              />
              <FormField
                className={styles.scopeField}
                compact
                label="Organization"
                type="select"
                value={organizationId}
                options={[
                  { value: '', label: 'All organizations' },
                  ...organizations.map((organization) => ({ value: organization.id, label: organization.name })),
                ]}
                onChange={(event) => {
                  setOrganizationId(event.target.value)
                  setOffering('')
                }}
              />
            </>
          ) : null}
          {!commandCenter ? (
            <FormField
              className={styles.scopeField}
              compact
              label="Surface"
              type="select"
              value={surface}
              options={surfaceOptions}
              onChange={(event) => setSurface(event.target.value)}
            />
          ) : null}
          {offeringOptions.length ? (
            <FormField
              className={styles.scopeField}
              compact
              label={commandCenter ? 'State' : 'Regulation'}
              type="select"
              value={offering}
              options={[
                { value: '', label: commandCenter ? 'All states' : 'All regulations' },
                ...offeringOptions,
              ]}
              onChange={(event) => setOffering(event.target.value)}
            />
          ) : null}
          {!commandCenter ? (
            <>
              {/* Archived posts are never live, so the archived view has no
                  book status to choose; its queue always asks for all posts. */}
              {visibility !== 'hidden' ? (
                <FormField
                  className={styles.scopeField}
                  compact
                  label="Book status"
                  type="select"
                  value={bookState}
                  options={BOOK_STATE_OPTIONS}
                  onChange={(event) => setBookState(event.target.value)}
                />
              ) : null}
              <FormField
                className={styles.scopeField}
                compact
                label="Post source"
                type="select"
                value={source}
                options={sourceOptions}
                onChange={(event) => setSource(event.target.value)}
              />
              {moderationHeldCount > 0 || moderationFilter === 'held' ? (
                <Button
                  className={styles.scopeToggle}
                  size="compact"
                  variant={moderationFilter === 'held' ? 'primary' : 'secondary'}
                  aria-pressed={moderationFilter === 'held'}
                  onClick={onToggleModerationHeld}
                >
                  {`Held by moderation (${moderationHeldCount ?? 0})`}
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
        <div className={styles.scopeStatus}>
          <div className={styles.scopeSummary} role="status" aria-live="polite" aria-busy={searchLoading || undefined}>
            <strong>{resultRange}</strong>
            {searchLoading ? <span>Updating…</span> : null}
          </div>
          <IconButton
            variant="ghost"
            label="Refresh posts"
            onClick={onRefresh}
            disabled={refreshPending}
            aria-busy={refreshPending || undefined}
          >
            <RefreshCw size={16} aria-hidden="true" />
          </IconButton>
        </div>
      </section>

      <FilterBar
        className={styles.queueFilterBar}
        label="Manage posts filters"
        query={creator.search}
        onQueryChange={(value) => setCreator((currentValue) => ({ ...currentValue, search: value }))}
        searchPlaceholder={commandCenter
          ? 'Search posts, creators, or organizations…'
          : 'Search titles, copy, or creators…'}
        searchLabel="Search posts"
        loading={searchLoading}
        advancedMode="drawer"
        drawerTitle="Filter posts"
        activeFilters={activeFilters}
        onRemoveFilter={onRemoveFilter}
        onClearAll={onClearAdvancedFilters}
        advancedFilters={(
          <div className={styles.filterDrawerGrid}>
            {commandCenter ? (
              <>
                <FormField
                  label="Surface"
                  type="select"
                  value={surface}
                  options={surfaceOptions}
                  onChange={(event) => setSurface(event.target.value)}
                />
                <FormField
                  label="Post source"
                  type="select"
                  value={source}
                  options={sourceOptions}
                  onChange={(event) => setSource(event.target.value)}
                />
              </>
            ) : null}
            <FormField
              label="Approval"
              type="select"
              value={status}
              options={STATUS_OPTIONS}
              onChange={(event) => setStatus(event.target.value)}
            />
            {!archivedOnly ? (
              <FormField
                label="Visibility"
                type="select"
                value={visibility}
                options={VISIBILITY_OPTIONS}
                onChange={(event) => setVisibility(event.target.value)}
              />
            ) : null}
            <FormField
              label="Content type"
              type="select"
              value={contentType}
              options={CONTENT_TYPE_OPTIONS}
              onChange={(event) => {
                const nextContentType = event.target.value
                setContentType(nextContentType)
                if (nextContentType !== 'parlay') {
                  setParlayStatus('')
                  setParlayOrigin('')
                }
              }}
            />
            {contentType === 'parlay' ? (
              <>
                <FormField
                  label="Parlay status"
                  type="select"
                  value={parlayStatus}
                  options={PARLAY_STATUS_OPTIONS}
                  onChange={(event) => setParlayStatus(event.target.value)}
                />
                <FormField
                  label="Parlay origin"
                  type="select"
                  value={parlayOrigin}
                  options={PARLAY_ORIGIN_OPTIONS}
                  onChange={(event) => setParlayOrigin(event.target.value)}
                />
              </>
            ) : null}
            <FormField
              label="Tag"
              type="select"
              value={tagId}
              options={[
                { value: '', label: 'All tags' },
                ...availableTags.map((tag) => ({ value: tag.id, label: tag.label })),
              ]}
              onChange={(event) => setTagId(event.target.value)}
            />
            {!commandCenter ? (
              <FormField
                label="Creator"
                value={creator.filter}
                onChange={(event) => setCreator((currentValue) => ({ ...currentValue, filter: event.target.value }))}
                placeholder="Creator name"
              />
            ) : null}
            {commandCenter ? (
              <div className={styles.filterDrawerToggles}>
                <Toggle
                  label="Only posts in the live feed"
                  checked={bookState === 'live'}
                  onChange={(checked) => setBookState(checked ? 'live' : 'all')}
                />
                <Toggle
                  label="Include personalized bets"
                  checked={includePersonalized}
                  onChange={(checked) => setIncludePersonalized(checked)}
                />
              </div>
            ) : null}
          </div>
        )}
      />

      {!commandCenter && canSelectPosts ? (
        <BulkActionBar
          count={selectedCount}
          noun="post"
          scope={scopeLabel}
          note={canBulkDelete && protectedSelectedCount > 0 ? (
            <>
              {protectedSelectedCount} cannot be deleted
              <HelpTip text={deleteRestrictionText} label="Why some selected posts cannot be deleted" />
            </>
          ) : null}
          onClear={onClearSelection}
          pending={selectionMutationPending}
          sticky
          ariaLabel="Selected post actions"
        >
          {canBulkVisibility && selectedArchivedCount > 0 ? (
            <Button
              variant="secondary"
              size="compact"
              onClick={onRestoreSelected}
              disabled={selectionMutationPending}
              loading={visibilityActionPending === 'restore'}
            >
              Restore {selectedArchivedCount}
            </Button>
          ) : null}
          {canBulkVisibility && selectedVisibleCount > 0 ? (
            <Button
              variant="secondary"
              size="compact"
              onClick={onArchiveSelected}
              disabled={selectionMutationPending}
              loading={visibilityActionPending === 'archive'}
            >
              Archive {selectedVisibleCount}
            </Button>
          ) : null}
          {canBulkDelete && deletableSelectedCount > 0 ? (
            <Button
              variant="danger"
              size="compact"
              onClick={onDeleteSelected}
              disabled={selectionMutationPending}
              loading={deletePending}
            >
              Delete {deletableSelectedCount} eligible
            </Button>
          ) : null}
        </BulkActionBar>
      ) : null}

      {commandCenter && canBulkCommand && selectedCount > 0 ? (
        <BulkActionBar
          count={selectedCount}
          noun="post"
          scope={bulkScopeSummary}
          note={bulkGroupedSelectedCount > 0 ? (
            <>
              {bulkGroupedSelectedCount} distributed
              <HelpTip
                text="Distributed posts share content across every book they run in. Archive and Restore apply to the whole distributed post; deleting or promoting one runs through the Distributed post editor; visibility, order, and limit changes apply per regulation here."
                label="How distributed posts behave in bulk actions"
              />
            </>
          ) : null}
          onClear={onClearSelection}
          pending={bulkCommandPending}
          sticky
          ariaLabel="Selected post bulk actions"
        >
          {bulkRestorableCount > 0 ? (
            <Button
              variant="secondary"
              size="compact"
              onClick={onBulkRestore}
              disabled={bulkCommandPending}
              loading={bulkCommandPendingKind === 'restore'}
            >
              Restore {bulkRestorableCount}
            </Button>
          ) : null}
          {bulkArchivableCount > 0 ? (
            <Button
              variant="secondary"
              size="compact"
              onClick={onBulkArchive}
              disabled={bulkCommandPending}
              loading={bulkCommandPendingKind === 'archive'}
            >
              Archive {bulkArchivableCount}
            </Button>
          ) : null}
          {bulkMoveToTopEligibleCount > 0 ? (
            <Button
              variant="secondary"
              size="compact"
              onClick={onBulkMoveToTop}
              disabled={bulkCommandPending}
              loading={bulkCommandPendingKind === 'move_to_top'}
            >
              Move {bulkMoveToTopEligibleCount} to top
            </Button>
          ) : null}
          {bulkPromoteEligibleCount > 0 ? (
            <Button
              variant="secondary"
              size="compact"
              onClick={onBulkPromote}
              disabled={bulkCommandPending}
              loading={bulkCommandPendingKind === 'promote'}
            >
              Promote {bulkPromoteEligibleCount}
            </Button>
          ) : null}
          {bulkDemoteEligibleCount > 0 ? (
            <Button
              variant="secondary"
              size="compact"
              onClick={onBulkDemote}
              disabled={bulkCommandPending}
              loading={bulkCommandPendingKind === 'demote'}
            >
              Remove promotion {bulkDemoteEligibleCount}
            </Button>
          ) : null}
          {bulkDeleteEligibleCount > 0 ? (
            <Button
              variant="danger"
              size="compact"
              onClick={onBulkDeleteDrafts}
              disabled={bulkCommandPending}
              loading={bulkCommandPendingKind === 'delete_drafts'}
            >
              Mark {bulkDeleteEligibleCount} for deletion
            </Button>
          ) : null}
          <div className={styles.bulkLimitControl}>
            <Input
              type="number"
              min="1"
              max="50"
              step="1"
              aria-label="Visible item limit"
              placeholder="Limit"
              value={bulkVisibleItemLimitInput}
              onChange={(event) => setBulkVisibleItemLimitInput(event.target.value)}
              disabled={bulkCommandPending}
            />
            <Button
              variant="secondary"
              size="compact"
              onClick={onBulkSetVisibleItemLimit}
              disabled={bulkCommandPending || !bulkVisibleItemLimitInput}
              loading={bulkCommandPendingKind === 'set_visible_item_limit'}
            >
              Change visible limit
            </Button>
          </div>
        </BulkActionBar>
      ) : null}
    </>
  )
}

// archivedOnly mounts the same queue as the org Archived posts view of the
// Library section: visibility is pinned to archived so operators land directly
// on restorable/deletable posts (issue #235, now part of #307).
function FeedOpsPage({ commandCenter = false, archivedOnly = false }) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentOrg, switchOrganization, hasProductAccess } = useOrganization()
  // Gate the distributed-post affordances on the operator's *capability*, not
  // on which mount is rendering. `commandCenter` says which page this is;
  // `commandCenterAccess` says whether this user may follow a link into it.
  // A client tenant has neither, and must not be told a post is distributed —
  // an internal operator browsing the org dashboard keeps the shortcut.
  const { hasRole, commandCenterAccess } = useAuth()
  const queryClient = useQueryClient()
  const canForceDelete = hasRole(ROLES.INTERNAL_ORG_ADMIN)
  const inlineInspector = useInlineInspector()
  const slug = currentOrg?.slug
  const organizationContextId = currentOrg?.id
  const headerTitle = archivedOnly ? ARCHIVED_POSTS_SECTION.label : 'Manage posts'
  const headerContext = archivedOnly ? FEED_LIBRARY_EYEBROW : undefined
  // Where a post marked for deletion can be found and restored: Command
  // Center has a Pending deletion tab; a book finds it among archived posts.
  const pendingDeleteLocation = commandCenter
    ? 'Pending deletion'
    : `${FEED_LIBRARY_SECTION_LABEL} › ${ARCHIVED_POSTS_SECTION.label}`
  const isEditor = hasRole(ROLES.EDITOR)
  const canPublishPosts = commandCenter || (
    hasProductAccess(PRODUCT_KEYS.FEED, 'publish') && isEditor
  )
  const canOperatePosts = !commandCenter && (
    (
      hasProductAccess(PRODUCT_KEYS.FEED, 'configure')
      || hasProductAccess(PRODUCT_KEYS.FEED, 'operate')
    ) && isEditor
  )
  const canEditPosts = canPublishPosts || canOperatePosts
  // canOrderPosts is defined further down, next to hasNarrowingFilters:
  // in Command Center reordering additionally depends on an exact book +
  // regulation scope and an unfiltered list, which are only known there.
  // /dashboard/feed/preview is an internal_org_admin route; hide the link rather
  // than sending operators to a permission wall.
  const canPreviewSurface = !commandCenter
    && hasProductAccess(PRODUCT_KEYS.FEED, 'configure')
    && hasRole(ROLES.INTERNAL_ORG_ADMIN)
  const canApproveGenerated = !commandCenter
    && hasProductAccess(PRODUCT_KEYS.FEED, 'publish')
    && hasRole(ROLES.INTERNAL_ORG_ADMIN)
  const canFilterGenerated = commandCenter || hasRole(ROLES.INTERNAL_ORG_ADMIN)
  const sourceOptions = canFilterGenerated ? SOURCE_OPTIONS : OPERATOR_SOURCE_OPTIONS
  const sourceValues = canFilterGenerated ? SOURCE_VALUES : OPERATOR_SOURCE_VALUES
  const [initialFilters] = useState(() => {
    const filters = readLegacyQueueFilters(searchParams, commandCenter, sourceValues)
    return archivedOnly ? { ...filters, visibility: 'hidden' } : filters
  })
  // Legacy parlay generation entrypoint redirects to Prepacks schedules. The
  // filter URL sync below must not rewrite the location first, or its replace
  // navigation wins over the redirect.
  const legacyParlayRedirect = !commandCenter && searchParams.get('workspace') === 'parlays'
  useEffect(() => {
    if (!legacyParlayRedirect) return
    navigate('/dashboard/prepacks/automations/schedules', { replace: true })
  }, [legacyParlayRedirect, navigate])
  const lastSyncedSearchRef = useRef(searchParams.toString())
  const lastSyncedOfferingRef = useRef(initialFilters.offering)
  const [surface, setSurface] = useState(initialFilters.surface)
  const [offering, setOffering] = useState(initialFilters.offering)
  const [searchAndCreator, setSearchAndCreator] = useState({
    search: initialFilters.search,
    filter: initialFilters.creator,
  })
  const [status, setStatus] = useState(initialFilters.status)
  const [visibility, setVisibility] = useState(initialFilters.visibility)
  const [bookState, setBookState] = useState(initialFilters.bookState)
  const [moderationFilter, setModerationFilter] = useState(initialFilters.moderation)
  const [source, setSource] = useState(initialFilters.source)
  const [contentType, setContentType] = useState(initialFilters.contentType)
  const [parlayStatus, setParlayStatus] = useState(initialFilters.parlayStatus)
  const [parlayOrigin, setParlayOrigin] = useState(initialFilters.parlayOrigin)
  const [generationRunIds, setGenerationRunIds] = useState(initialFilters.generationRunIds)
  const [lifecycle, setLifecycle] = useState(initialFilters.lifecycle)
  const [includePersonalized, setIncludePersonalized] = useState(initialFilters.includePersonalized)
  const [tagId, setTagId] = useState('')
  const [page, setPage] = useState(1)
  const [placementItem, setPlacementItem] = useState(null)
  const [queueDraft, setQueueDraft] = useState([])
  const [queueDraftContext, setQueueDraftContext] = useState('')
  const [selectedPostIds, setSelectedPostIds] = useState([])
  // The All Regulations overview selects families (one logical post each),
  // keyed by family_key, on the current page only.
  const [selectedFamilyKeys, setSelectedFamilyKeys] = useState([])
  const [draggedId, setDraggedId] = useState(null)
  const [providerId, setProviderId] = useState('')
  const [organizationId, setOrganizationId] = useState('')
  const [orderingForm, setOrderingForm] = useState({
    pin_to_top: false,
    placement_strategy: 'manual',
    boost_factor: 1,
    apply_until: '',
    notes: '',
  })
  const [confirmAction, setConfirmAction] = useState(null)
  // Command Center only: the held row whose moderation override is open.
  const [moderationItem, setModerationItem] = useState(null)
  const [rankAnnouncement, setRankAnnouncement] = useState('')
  const [inspectorRowId, setInspectorRowId] = useState(null)
  const [inspectorScope, setInspectorScope] = useState('')
  const [pendingDraftInspectorId, setPendingDraftInspectorId] = useState(() => searchParams.get('draft') || null)
  const hoverPreview = useHoverPreviewAnchor()
  const [hoverPreviewItem, setHoverPreviewItem] = useState(null)
  const [overviewDraft, setOverviewDraft] = useState([])
  const [overviewMoveToTopPending, setOverviewMoveToTopPending] = useState(false)
  const overviewMoveRequestRef = useRef(0)
  const [overviewDraggedKey, setOverviewDraggedKey] = useState(null)
  const [offeringWarning, setOfferingWarning] = useState('')
  const queueFocusRef = useRef(null)
  // A row's moderation badge says why the post is not live yet. While one
  // would change on its own (a check still running), the list refetches so the
  // badge clears when the post goes live; never while the operator holds an
  // unsaved order, which a refetch would replace.
  const [moderationPollPaused, setModerationPollPaused] = useState(false)
  // Each list's fast-poll clock (see moderationRowsPollInterval).
  const queuePollTrackerRef = useRef(null)
  const overviewPollTrackerRef = useRef(null)
  const focusQueue = () => window.queueMicrotask(() => queueFocusRef.current?.focus())
  const pendingQueueFocusRef = useRef(false)
  const generatedPublishIntentKeysRef = useRef(new Map())
  const pendingRowFocusIdRef = useRef(null)
  const canBulkVisibility = offering ? canOperatePosts : canPublishPosts
  const canBulkDelete = canPublishPosts
  const canSelectPosts = canBulkVisibility || canBulkDelete
  // Command Center selection/bulk commands are provider-scoped: browsing can
  // span every provider, but no target can be built (and no checkbox is
  // shown) until exactly one provider is chosen.
  const canBulkCommand = commandCenter && Boolean(providerId)
  const bulkMoveAnchorRef = useRef(null)
  const [bulkCommandResult, setBulkCommandResult] = useState(null)
  const [bulkVisibleItemLimitInput, setBulkVisibleItemLimitInput] = useState('')
  // Extends a saved Command Center order from the selected state to every
  // state the book runs in — the "order one whole brand" ask.
  const [applyOrderToAllStates, setApplyOrderToAllStates] = useState(false)
  const updateFilter = (setter, value) => {
    setPage(1)
    setter(value)
  }

  const providersQuery = useQuery({
    queryKey: ['command-center', 'providers'],
    queryFn: () => commandCenterApi.listProviders(),
    enabled: commandCenter,
  })

  const organizationsQuery = useQuery({
    queryKey: ['command-center', 'organizations', providerId],
    queryFn: () => commandCenterApi.listOrganizations(providerId ? { provider_id: providerId } : {}),
    enabled: commandCenter,
  })

  // Resolves the exact feed_surfaces row backing the current `surface` filter:
  // the per-org dashboard's own book (`slug`), or Command Center's selected
  // book once one exact organization is chosen. Command Center's
  // 'default-feed'/'widget' filter names a real per-book slug
  // (buildFeedOpsQueuePredicates's book_state=live join joins feed_surfaces
  // on it directly) — it is only unresolvable before a single book is
  // selected.
  const activeOrganizationSlug = commandCenter
    ? organizationsQuery.data?.organizations?.find((organization) => organization.id === organizationId)?.slug || ''
    : slug
  const surfacesQuery = useQuery({
    queryKey: ['feed-ops-surfaces', activeOrganizationSlug],
    queryFn: () => surfacesApi.list(activeOrganizationSlug),
    enabled: Boolean(activeOrganizationSlug),
    staleTime: 60_000,
  })


  const offeringsQuery = useQuery({
    queryKey: ['feed-ops-offerings', slug],
    queryFn: () => kambiApi.getOfferings(slug),
    enabled: Boolean(slug) && !commandCenter,
    retry: false,
    staleTime: 60_000,
  })

  const brandingQuery = useQuery({
    queryKey: ['feed-ops-branding', organizationContextId],
    queryFn: () => organizationApi.getBranding(organizationContextId),
    enabled: Boolean(organizationContextId) && !commandCenter,
    staleTime: 300_000,
  })

  const offeringOptions = useMemo(() => {
    const data = commandCenter
      ? organizationsQuery.data?.organizations?.find((organization) => organization.id === organizationId)
      : offeringsQuery.data
    const regulations = new Map(organizationRegulationMappings(data)
      .filter((mapping) => mapping.regulation_name)
      .map((mapping) => [mapping.offering_id, mapping.regulation_name]))
    const entries = (data?.offering_ids || data?.kambi_offering_ids || []).map((offeringId) => ({
      offeringId,
      regulation: formatRegulationName(regulations.get(offeringId)),
    }))
    const bookLabels = bookRegulationLabels(entries)
    return entries.map(({ offeringId, regulation }) => {
      if (!regulation) return { value: offeringId, label: offeringId, regulation: '' }
      return {
        value: offeringId,
        label: commandCenter ? `${regulation} (${offeringId})` : bookLabels.get(offeringId),
        regulation,
      }
    })
  }, [commandCenter, offeringsQuery.data, organizationId, organizationsQuery.data?.organizations])

  const offeringScopeResolved = commandCenter ? organizationsQuery.isFetched : offeringsQuery.isFetched
  const overviewAvailable = !commandCenter && offeringScopeResolved && offeringOptions.length > 0
  const isOverview = overviewAvailable && !offering
  const offeringRecognized = !offering || offeringOptions.some((option) => option.value === offering)
  const selectedOfferingLabel = offering
    ? offeringOptions.find((option) => option.value === offering)?.label || offering
    : ''

  // Regulation is the operator-facing noun; the provider's offering code is only
  // ever a disambiguator for regulations that map to more than one code. An
  // unmapped code has no regulation name to show — callers fall back to the code
  // itself, which the operator recognises, rather than labelling it "unknown".
  const regulationFor = useMemo(() => {
    const labels = new Map()
    ;(organizationsQuery.data?.organizations || []).forEach((organization) => {
      organizationRegulationMappings(organization).forEach((mapping) => {
        if (mapping.regulation_name && !labels.has(mapping.offering_id)) {
          labels.set(mapping.offering_id, formatRegulationName(mapping.regulation_name))
        }
      })
    })
    offeringOptions.forEach((option) => {
      if (option.regulation) labels.set(option.value, option.regulation)
    })
    return (offeringId) => labels.get(offeringId) || ''
  }, [offeringOptions, organizationsQuery.data?.organizations])


  const surfaceOptions = useMemo(() => {
    if (commandCenter) {
      // Once one exact organization is chosen its real surface list is
      // loaded, so offering a surface that book does not have would only
      // produce a silent zero-result queue.
      const orgSlugs = organizationId && surfacesQuery.isFetched
        ? new Set((surfacesQuery.data?.items || [])
          .filter((item) => item.is_enabled !== false)
          .map((item) => item.slug))
        : null
      if (orgSlugs?.size) {
        const scoped = COMMAND_CENTER_SURFACE_OPTIONS.filter((option) => orgSlugs.has(option.value))
        if (scoped.length) return scoped
      }
      return COMMAND_CENTER_SURFACE_OPTIONS
    }

    const surfaces = (surfacesQuery.data?.items || []).filter((item) => item.is_enabled !== false)
    if (!surfaces.length) {
      return [{ value: DEFAULT_SURFACE_SLUG, label: 'Feed' }]
    }

    return surfaces.map((item) => ({
      value: item.slug,
      label: item.name || item.slug,
      includeByDefault: Boolean(item.include_by_default),
    }))
  }, [commandCenter, organizationId, surfacesQuery.data?.items, surfacesQuery.isFetched])


  const defaultSurface = useMemo(() => (
    surfaceOptions.find((option) => option.includeByDefault)?.value || surfaceOptions[0]?.value || DEFAULT_SURFACE_SLUG
  ), [surfaceOptions])
  const surfaceLabel = surfaceOptions.find((option) => option.value === surface)?.label || surface

  useEffect(() => {
    if ((!commandCenter && !surfacesQuery.isFetched) || !surfaceOptions.length) return
    if (surfaceOptions.some((option) => option.value === surface)) return

    setPage(1)
    setSurface(defaultSurface)
  }, [commandCenter, defaultSurface, surface, surfaceOptions, surfacesQuery.isFetched])

  useEffect(() => {
    setPage(1)
  }, [commandCenter, organizationContextId, organizationId, providerId, slug])

  useEffect(() => {
    if (!offering) return
    if (!offeringScopeResolved || offeringOptions.some((option) => option.value === offering)) return
    setPage(1)
    setOffering('')
    setOfferingWarning('That regulation is no longer available for this organization. Showing all regulations.')
  }, [offering, offeringOptions, offeringScopeResolved])

  useEffect(() => {
    if (!offeringWarning || !offering) return
    setOfferingWarning('')
  }, [offering, offeringWarning])

  useEffect(() => {
    if (legacyParlayRedirect) return
    const currentSearch = searchParams.toString()
    if (currentSearch !== lastSyncedSearchRef.current) {
      const filters = readLegacyQueueFilters(searchParams, commandCenter, sourceValues)
      lastSyncedSearchRef.current = currentSearch
      setPage(1)
      setSurface(filters.surface)
      setSearchAndCreator((currentValue) => (
        currentValue.search === filters.search && currentValue.filter === filters.creator
          ? currentValue
          : { search: filters.search, filter: filters.creator }
      ))
      setStatus(filters.status)
      setVisibility(archivedOnly ? 'hidden' : filters.visibility)
      setBookState(filters.bookState)
      setModerationFilter(filters.moderation)
      setSource(filters.source)
      setContentType(filters.contentType)
      setParlayStatus(filters.parlayStatus)
      setParlayOrigin(filters.parlayOrigin)
      setGenerationRunIds(filters.generationRunIds)
      setLifecycle(filters.lifecycle)
      setIncludePersonalized(filters.includePersonalized)
      if (!commandCenter) {
        lastSyncedOfferingRef.current = filters.offering
        setOffering(filters.offering)
      }
      const draftParam = searchParams.get('draft')
      if (draftParam) setPendingDraftInspectorId(draftParam)
      return
    }

    const normalized = new URLSearchParams()
    if (searchAndCreator.search) normalized.set('search', searchAndCreator.search)
    if (surface !== DEFAULT_SURFACE_SLUG) normalized.set('surface', surface)
    if (!commandCenter && searchAndCreator.filter) normalized.set('creator', searchAndCreator.filter)
    if (!commandCenter && offering) normalized.set('offering', offering)
    if (status) normalized.set('status', status)
    if (!archivedOnly && visibility) normalized.set('visibility', visibility)
    if (bookState !== 'live') normalized.set('book_state', bookState)
    if (!commandCenter && moderationFilter) normalized.set('moderation', moderationFilter)
    if (source !== 'creator') normalized.set('source', source)
    if (contentType) normalized.set('content_type', contentType)
    if (parlayStatus) normalized.set('parlay_status', parlayStatus)
    if (parlayOrigin) normalized.set('parlay_origin', parlayOrigin)
    if (generationRunIds) normalized.set('generation_run_id', generationRunIds)
    if (commandCenter && lifecycle !== 'active') normalized.set('lifecycle', lifecycle)
    if (commandCenter && includePersonalized) normalized.set('include_personalized', 'true')
    const nextSearch = normalized.toString()
    if (nextSearch !== currentSearch) {
      const scopeChanged = !commandCenter && lastSyncedOfferingRef.current !== offering
      lastSyncedOfferingRef.current = offering
      lastSyncedSearchRef.current = nextSearch
      setSearchParams(normalized, { replace: !scopeChanged })
    }
  }, [
    archivedOnly,
    bookState,
    commandCenter,
    contentType,
    canEditPosts,
    generationRunIds,
    legacyParlayRedirect,
    includePersonalized,
    lifecycle,
    moderationFilter,
    parlayOrigin,
    parlayStatus,
    searchAndCreator.filter,
    searchAndCreator.search,
    offering,
    searchParams,
    setSearchParams,
    source,
    sourceValues,
    status,
    surface,
    visibility,
  ])

  const sharedQueueParams = useMemo(() => ({
    limit: QUEUE_PAGE_SIZE,
    cursor: (page - 1) * QUEUE_PAGE_SIZE,
    surface,
    ...(searchAndCreator.search ? { search: searchAndCreator.search } : {}),
    ...(status ? { status } : {}),
    ...(visibility ? { visibility } : {}),
    // Archived/All lifecycle tabs must not stay scoped to book_state=live:
    // that join requires is_published=TRUE plus a live-visibility check
    // mirroring is_hidden, and book_state=live additionally requires the
    // post's lifecycle to be 'active', so a genuinely archived (is_hidden) or
    // pending/rejected/expired post is silently dropped even though the
    // lifecycle filter asked for it. Active keeps the operator's own
    // book_state choice (default 'live') unchanged.
    // The same holds for the archived view (visibility=hidden): an archived
    // post is never live, so book_state=live would always come back empty.
    book_state: ((commandCenter && lifecycle !== 'active') || visibility === 'hidden') ? 'all' : bookState,
    ...(!commandCenter && moderationFilter ? { moderation: moderationFilter } : {}),
    source,
    ...(contentType ? { content_type: contentType } : {}),
    ...(contentType === 'parlay' && parlayStatus ? { parlay_status: parlayStatus } : {}),
    ...(contentType === 'parlay' && parlayOrigin ? { parlay_origin: parlayOrigin } : {}),
    ...(tagId ? { tag_ids: tagId } : {}),
    ...(generationRunIds ? { generation_run_id: generationRunIds } : {}),
    // Lifecycle/personalized scope only exists for the central endpoint;
    // the per-org dashboard query is left byte-identical to before.
    ...(commandCenter ? { lifecycle, include_personalized: includePersonalized } : {}),
  }), [
    bookState,
    commandCenter,
    contentType,
    includePersonalized,
    lifecycle,
    moderationFilter,
    page,
    generationRunIds,
    parlayOrigin,
    parlayStatus,
    searchAndCreator.search,
    source,
    status,
    surface,
    tagId,
    visibility,
  ])

  const queueQueryKey = useMemo(() => [
    'feed-ops',
    'queue',
    commandCenter ? 'command-center' : slug,
    offering,
    searchAndCreator.filter,
    providerId,
    organizationId,
    sharedQueueParams,
  ], [commandCenter, offering, organizationId, providerId, searchAndCreator.filter, sharedQueueParams, slug])
  const overviewQueryKey = useMemo(() => [
    'feed-ops',
    'overview',
    slug,
    searchAndCreator.filter,
    sharedQueueParams,
  ], [searchAndCreator.filter, sharedQueueParams, slug])
  const queueRequestKey = useMemo(() => JSON.stringify(queueQueryKey), [queueQueryKey])

  const queueQuery = useQuery({
    queryKey: queueQueryKey,
    queryFn: () => commandCenter
      ? commandCenterApi.listFeedOpsQueue({
        ...sharedQueueParams,
        ...(offering ? { offering } : {}),
        ...(providerId ? { provider_id: providerId } : {}),
        ...(organizationId ? { organization_id: organizationId } : {}),
      })
      : feedOpsApi.listQueue(slug, {
        ...sharedQueueParams,
        ...(offering ? { offering } : {}),
        ...(searchAndCreator.filter ? { creator: searchAndCreator.filter } : {}),
      }),
    enabled: commandCenter || (Boolean(slug) && offeringScopeResolved && offeringRecognized && !isOverview),
    placeholderData: (previousData) => previousData,
    refetchInterval: (query) => {
      if (moderationPollPaused) {
        queuePollTrackerRef.current = null
        return false
      }
      // Command Center rows are families: every member's badge can change.
      const rows = (query.state.data?.items || []).flatMap((item) => (commandCenter
        ? rowMembers(item).map((member) => ({ key: member.post_id, moderation: member.moderation }))
        : [{ key: item?.id, moderation: item?.moderation }]))
      const { interval, tracker } = moderationRowsPollInterval(rows, queuePollTrackerRef.current)
      queuePollTrackerRef.current = tracker
      return interval
    },
  })

  const overviewQuery = useQuery({
    queryKey: overviewQueryKey,
    queryFn: () => feedOpsApi.listQueue(slug, {
      ...sharedQueueParams,
      ...(searchAndCreator.filter ? { creator: searchAndCreator.filter } : {}),
      view: 'overview',
    }),
    enabled: isOverview && Boolean(slug),
    placeholderData: (previousData) => previousData,
    refetchInterval: (query) => {
      if (moderationPollPaused) {
        overviewPollTrackerRef.current = null
        return false
      }
      const rows = (query.state.data?.items || []).map((family) => ({
        key: family?.family_key || family?.representative?.id,
        moderation: family?.representative?.moderation,
      }))
      const { interval, tracker } = moderationRowsPollInterval(rows, overviewPollTrackerRef.current)
      overviewPollTrackerRef.current = tracker
      return interval
    },
  })

  const queueContextMatches = !isOverview && queueDraftContext === queueRequestKey
  const queueInteractionLocked = !queueContextMatches || queueQuery.isLoading || queueQuery.isPlaceholderData
  const overviewServerItems = useMemo(() => overviewQuery.data?.items || [], [overviewQuery.data?.items])
  const overviewPageOffset = (page - 1) * QUEUE_PAGE_SIZE
  const isGlobalOverviewDraft = overviewDraft.length > QUEUE_PAGE_SIZE
  const overviewItems = overviewDraft.length
    ? overviewDraft.slice(isGlobalOverviewDraft ? overviewPageOffset : 0, (isGlobalOverviewDraft ? overviewPageOffset : 0) + QUEUE_PAGE_SIZE)
    : overviewServerItems
  const overviewOrderFamilies = overviewDraft.length ? overviewDraft : overviewItems
  const overviewOrderOffset = isGlobalOverviewDraft ? 0 : overviewPageOffset
  const isOverviewOrderDirty = overviewDraft.length > 0
    && (isGlobalOverviewDraft
      || overviewItems.some((family, index) => family.family_key !== overviewServerItems[index]?.family_key))
  const activeQuery = isOverview ? overviewQuery : queueQuery
  // Posts moderation holds or rejected in the current scope, in every book
  // state: the number on the "Held by moderation" toggle.
  const moderationHeldCount = commandCenter ? null : (activeQuery.data?.moderation_held_count ?? null)
  const toggleModerationHeld = () => {
    setPage(1)
    if (moderationFilter === 'held') {
      setModerationFilter('')
      return
    }
    // A held post has usually left the live feed: list every book state.
    setModerationFilter('held')
    setBookState('all')
  }
  const listInteractionLocked = isOverview
    ? (overviewQuery.isLoading || overviewQuery.isPlaceholderData || overviewMoveToTopPending)
    : queueInteractionLocked

  const selectedPosts = useMemo(
    () => queueDraft.filter((item) => selectedPostIds.includes(item.id)),
    [queueDraft, selectedPostIds],
  )
  const deletableSelectedIds = useMemo(
    () => selectedPosts
      .filter((item) => item.delete_capability?.allowed && item.lifecycle !== 'pending_delete')
      .map((item) => item.id),
    [selectedPosts],
  )
  const selectedVisibleIds = useMemo(
    () => selectedPosts.filter((item) => !item.is_hidden).map((item) => item.id),
    [selectedPosts],
  )
  const restorableSelectedIds = useMemo(
    () => selectedPosts
      .filter((item) => item.is_hidden && !isGeneratedParlay(item) && item.lifecycle !== 'pending_delete')
      .map((item) => item.id),
    [selectedPosts],
  )
  // Bulk erase targets: standalone rows marked for deletion, each addressed to
  // its own book (Command Center spans books). Distributed rows are skipped:
  // batch-force-delete refuses group_managed and a group has no erase endpoint.
  const eraseTargets = useMemo(() => selectedPosts
    .filter((item) => (item.lifecycle === 'pending_delete' || Boolean(item.pending_delete_at)) && !item.post_group_id)
    .map((item) => ({ postId: item.id, orgSlug: item.organization_slug || slug })), [selectedPosts, slug])
  const protectedSelectedCount = selectedPosts.length - deletableSelectedIds.length
  const allLoadedSelected = queueDraft.length > 0 && queueDraft.every((item) => selectedPostIds.includes(item.id))

  // Overview selection expands to this book's member posts per action; a
  // family counts toward an action when any member is eligible, except
  // delete, which needs every member to be deletable.
  const overviewSelection = useMemo(() => {
    const selectedFamilies = overviewItems.filter((family) => selectedFamilyKeys.includes(family.family_key))
    const familiesWith = (action) => {
      const families = selectedFamilies.filter((family) => familyMemberIds(family, action).length > 0)
      return {
        familyCount: families.length,
        postIds: [...new Set(families.flatMap((family) => familyMemberIds(family, action)))],
      }
    }
    const deletable = selectedFamilies.filter(familyIsDeletable)
    return {
      families: selectedFamilies,
      archive: familiesWith('archive'),
      restore: familiesWith('restore'),
      delete: {
        families: deletable,
        familyCount: deletable.length,
        postIds: [...new Set(deletable.flatMap((family) => familyMemberIds(family, 'delete')))],
      },
      protectedCount: selectedFamilies.length - deletable.length,
      distributedCount: selectedFamilies.filter(familyIsDistributed).length,
    }
  }, [overviewItems, selectedFamilyKeys])
  const allOverviewSelected = overviewItems.length > 0
    && overviewItems.every((family) => selectedFamilyKeys.includes(family.family_key))

  // The drawer is addressed by the row's canonical post id rather than by a
  // snapshot of the family, so a refetch behind an open drawer refreshes it
  // instead of leaving stale content on screen.
  const inspectorFamily = useMemo(() => {
    if (!inspectorRowId) return null
    if (isOverview) {
      return overviewItems.find((family) => familyRootId(family) === inspectorRowId) || null
    }
    const item = queueDraft.find((entry) => entry.id === inspectorRowId)
    return item ? queueItemAsFamily(item, offering) : null
  }, [inspectorRowId, isOverview, offering, overviewItems, queueDraft])

  // Every regulation in a family is served by exactly one post, so the scope
  // selector maps straight onto that row's id. '' means the shared copy.
  const inspectorScopePostId = useMemo(() => {
    if (!inspectorScope) return inspectorRowId
    const variant = (inspectorFamily?.variants || []).find((entry) => entry.offering_id === inspectorScope)
    return variant?.post_id || inspectorRowId
  }, [inspectorFamily, inspectorRowId, inspectorScope])

  const inspectorScopeOptions = useMemo(() => {
    if (!isOverview || !inspectorFamily) return []
    const seen = new Set()
    const options = [{ value: '', label: 'All regulations' }]
    for (const variant of inspectorFamily.variants || []) {
      const offeringId = variant.offering_id
      if (!offeringId || seen.has(offeringId)) continue
      seen.add(offeringId)
      const regulation = regulationFor(offeringId)
      const label = regulation ? `${regulation} (${offeringId})` : offeringId
      options.push({ value: offeringId, label: variant.is_variant ? `${label} — custom copy` : label })
    }
    // A single-regulation post cannot fork a regulation away from itself, so the
    // control would only ever offer a no-op.
    return options.length > 2 ? options : []
  }, [inspectorFamily, isOverview, regulationFor])

  const inspectorOrganizationId = inspectorFamily?.representative?.organization_id || ''
  const inspectorDetailQuery = useQuery({
    queryKey: commandCenter
      ? ['feed-ops', 'command-center-post-detail', inspectorScopePostId, inspectorOrganizationId, surface]
      : ['feed-ops', 'post-detail', slug, inspectorScopePostId, surface, inspectorScope || offering],
    queryFn: () => commandCenter
      ? commandCenterApi.getFeedOpsPost(inspectorScopePostId, {
        organization_id: inspectorOrganizationId,
        surface,
      })
      : feedOpsApi.getPostDetail(slug, inspectorScopePostId, {
        surface,
        ...(inspectorScope || offering ? { offering: inspectorScope || offering } : {}),
      }),
    enabled: Boolean(inspectorScopePostId) && (
      commandCenter ? Boolean(inspectorOrganizationId) : Boolean(slug)
    ),
  })

  // Command Center's preview/branding is scoped to the exact row's own
  // organization, never the operator's current OrganizationContext — the
  // dashboard's brandingQuery (keyed on organizationContextId) stays
  // untouched and unused here.
  const inspectorBrandingQuery = useQuery({
    queryKey: ['feed-ops-branding', 'command-center', inspectorOrganizationId],
    queryFn: () => commandCenterApi.getOrgBranding(inspectorOrganizationId),
    enabled: commandCenter && Boolean(inspectorOrganizationId),
    staleTime: 300_000,
  })

  // A grouped member's distributed-post membership (every other book in the
  // family) is read-only reference data for the inspector: which books, and
  // their exact lifecycle. It never changes global organization context and
  // never feeds the editable draft.
  const inspectorGroupId = inspectorFamily?.representative?.post_group_id || ''
  const inspectorGroupQuery = useQuery({
    queryKey: ['command-center', 'post-group', 'membership', inspectorGroupId],
    queryFn: () => commandCenterApi.getPostGroup(inspectorGroupId),
    enabled: commandCenter && Boolean(inspectorGroupId),
  })

  // The overview payload carries preview-only fields; the condensed form needs
  // the post's editable state, so the detail response is layered on top.
  const inspectorData = useMemo(() => {
    if (!inspectorFamily) return null
    const detail = inspectorDetailQuery.data?.post
    if (!detail) return inspectorFamily
    return {
      ...inspectorFamily,
      representative: { ...inspectorFamily.representative, ...detail },
    }
  }, [inspectorFamily, inspectorDetailQuery.data?.post])
  // Read from the merged record, not the row: the detail response is the
  // authoritative copy and fills in anything the list payload left out.
  const inspectorPostIsGrouped = Boolean(inspectorData?.representative?.post_group_id)
  const inspectorOrganizationSlug = commandCenter
    ? inspectorFamily?.representative?.organization_slug
    : slug
  const tagsQuery = useQuery({
    queryKey: ['tags', inspectorOrganizationSlug],
    queryFn: () => tagApi.list(inspectorOrganizationSlug, true),
    enabled: Boolean(inspectorOrganizationSlug) && (!commandCenter || Boolean(inspectorFamily)),
  })

  const refreshQueue = () => queryClient.invalidateQueries({ queryKey: ['feed-ops'] })
  // Drop overview families an action touched; untouched ones stay selected,
  // like protected posts in the queue. Success path only: after a partial
  // chunk failure families stay selected until the refetch decides.
  const pruneProcessedFamilies = (postIds) => {
    const processed = new Set(postIds)
    setSelectedFamilyKeys((currentValue) => currentValue.filter((key) => {
      const family = overviewItems.find((item) => item.family_key === key)
      return !familyMembers(family).some((member) => processed.has(member.post_id))
    }))
  }
  // A chunked action can fail after earlier chunks were applied: say so, drop
  // the applied posts from the queue selection (families stay selected until
  // the refetch decides), and refetch.
  const reportChunkedFailure = (error, base, { postIds = [] } = {}, pastVerb) => {
    const processed = error.processedPostIds || []
    if (!processed.length) {
      toast.error(base)
      refreshQueue()
      return
    }
    const total = Math.max(postIds.length, processed.length)
    toast.error(`${base} — ${processed.length} of ${total} posts were already ${pastVerb}; the rest were left unchanged`)
    setSelectedPostIds((currentValue) => currentValue.filter((postId) => !processed.includes(postId)))
    refreshQueue()
  }

  useEffect(() => {
    if (!isOverview) return
    setQueueDraft([])
    setQueueDraftContext('')
    setSelectedPostIds([])
    setPlacementItem(null)
    setDraggedId(null)
  }, [isOverview])

  useEffect(() => {
    if (isOverview) return
    setInspectorRowId(null)
    setSelectedFamilyKeys([])
  }, [isOverview])

  useEffect(() => {
    setOverviewDraft([])
    overviewMoveRequestRef.current += 1
    setOverviewMoveToTopPending(false)
    setOverviewDraggedKey(null)
    // Selection lives on one page under one set of filters.
    setSelectedFamilyKeys([])
  }, [isOverview, overviewQueryKey])

  useEffect(() => {
    if (!isOverview || overviewQuery.isPlaceholderData) return
    setSelectedFamilyKeys((currentValue) => currentValue.filter((key) => (
      overviewServerItems.some((family) => family.family_key === key)
    )))
  }, [isOverview, overviewServerItems, overviewQuery.isPlaceholderData])

  useEffect(() => {
    if (isOverview || !queueQuery.data || queueQuery.isPlaceholderData) return
    const items = queueQuery.data.items || []
    setQueueDraft(items)
    setQueueDraftContext(queueRequestKey)
    setSelectedPostIds((currentValue) => currentValue.filter((postId) => (
      items.some((item) => item.id === postId)
    )))
    setPlacementItem((currentValue) => (
      currentValue ? items.find((item) => item.id === currentValue.id) || null : null
    ))
  }, [isOverview, queueQuery.data, queueQuery.isPlaceholderData, queueRequestKey])

  useEffect(() => {
    if (!pendingDraftInspectorId || !queueContextMatches) return
    if (!queueDraft.some((item) => item.id === pendingDraftInspectorId)) return
    setInspectorScope('')
    setInspectorRowId(pendingDraftInspectorId)
    setPendingDraftInspectorId(null)
  }, [pendingDraftInspectorId, queueContextMatches, queueDraft])

  useEffect(() => {
    if (activeQuery.isPlaceholderData) return
    const total = activeQuery.data?.total
    if (typeof total !== 'number') return
    const limit = activeQuery.data?.limit || QUEUE_PAGE_SIZE
    const pageCount = Math.max(1, Math.ceil(total / limit))
    setPage((currentValue) => Math.min(currentValue, pageCount))
  }, [activeQuery.data?.limit, activeQuery.data?.total, activeQuery.isPlaceholderData])

  useEffect(() => {
    if (!pendingQueueFocusRef.current || isOverview || queueInteractionLocked) return
    pendingQueueFocusRef.current = false
    queueFocusRef.current?.focus()
  }, [isOverview, queueInteractionLocked])
  useEffect(() => {
    const postID = pendingRowFocusIdRef.current
    if (!postID || (!inlineInspector && inspectorRowId)) return
    if (!queueDraft.some((item) => item.id === postID)) return
    window.queueMicrotask(() => {
      const row = document.querySelector(`[data-testid="post-row-${postID}"]`)
      if (!row) return
      pendingRowFocusIdRef.current = null
      row.focus()
    })
  }, [inlineInspector, inspectorRowId, queueDraft])



  const bulkVisibilityMutation = useMutation({
    mutationFn: async ({ isHidden, postIds = selectedPosts.map((post) => post.id) }) => {
      if (offering) {
        return feedOpsApi.batchVisibility(slug, {
          post_ids: postIds,
          is_hidden: isHidden,
          surface,
          offering_id: offering,
        })
      }

      // Without a regulation the archive is post-level: one all-or-nothing
      // batch request per chunk of BATCH_POST_LIMIT posts.
      await runInChunks(postIds, (chunk) => postApi.archiveMany(slug, { postIds: chunk, archived: isHidden }))
      return { updated: postIds.length }
    },
    onSuccess: (_result, variables) => {
      const processedIds = variables.postIds || []
      const processedCount = variables.unitCount ?? processedIds.length
      toast.success(`${variables.isHidden ? 'Archived' : 'Restored'} ${processedCount} ${processedCount === 1 ? 'post' : 'posts'}`)
      setSelectedPostIds((currentValue) => currentValue.filter((postId) => !processedIds.includes(postId)))
      pruneProcessedFamilies(processedIds)
      focusQueue()
      refreshQueue()
    },
    // A refusal can land after an earlier chunk already changed posts, so
    // report what was applied and refetch to show the truth. On the
    // overview, name the refused family. group_archived stays neutral
    // server-side; only operators with Command Center access learn why.
    onError: (error, variables = {}) => {
      const code = error.response?.data?.code
      const message = code === 'group_archived' && commandCenterAccess
        ? 'This post is archived everywhere it runs — restore it from Command Center.'
        : (error.response?.data?.error || 'Failed to update visibility')
      const failedPostId = error.response?.data?.post_id
      const failedFamily = isOverview && failedPostId
        ? overviewItems.find((family) => familyMembers(family).some((member) => member.post_id === failedPostId))
        : null
      const base = failedFamily ? `${message} (${overviewFamilyLabel(failedFamily)})` : message
      reportChunkedFailure(error, base, variables, variables.isHidden ? 'archived' : 'restored')
    },
  })
  const generatedPublishMutation = useMutation({
    mutationFn: async (postID) => {
      let idempotencyKey = generatedPublishIntentKeysRef.current.get(postID)
      if (!idempotencyKey) {
        const nonce = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
        idempotencyKey = `publish-generated-${postID}-${nonce}`
        generatedPublishIntentKeysRef.current.set(postID, idempotencyKey)
      }
      const result = await parlayApi.publishGeneratedDraft(slug, postID, idempotencyKey)
      // The publish answer says nothing about moderation; read the post. A
      // failed read never fails the publish.
      let post = null
      try {
        post = await postApi.get(slug, postID)
      } catch {
        post = null
      }
      return { ...result, moderation: post?.moderation || null, moderationRead: Boolean(post) }
    },
    onSuccess: (result, postID) => {
      generatedPublishIntentKeysRef.current.delete(postID)
      const publishedIndex = queueDraft.findIndex((item) => item.id === postID)
      const remaining = queueDraft.filter((item) => item.id !== postID)
      const nextItem = remaining[Math.min(Math.max(publishedIndex, 0), remaining.length - 1)] || null
      setQueueDraft(remaining)
      setSelectedPostIds((currentValue) => currentValue.filter((selectedID) => selectedID !== postID))
      setInspectorRowId(inlineInspector ? nextItem?.id || null : null)
      pendingRowFocusIdRef.current = nextItem?.id || null
      const notice = moderationPublishNotice(result, 'Generated parlay approved')
      if (notice) {
        const notify = notice.tone === 'error' ? toast.error : toast.info
        setRankAnnouncement(notice.message)
        notify(notice.message)
      } else if (!result?.moderationRead) {
        setRankAnnouncement('Generated parlay approved. Check Manage posts for whether it is live.')
        toast.info('Generated parlay approved. Check Manage posts for whether it is live.')
      } else {
        setRankAnnouncement('Generated parlay approved and published')
        toast.success('Generated parlay approved and published')
      }
      if (!nextItem) focusQueue()
      refreshQueue()
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Generated parlay could not be published')
    },
  })


  const {
    saveOrderMutation, saveCommandCenterOrderMutation, resetOrderMutation,
    resetSurfaceOrderMutation, saveOverviewOrderMutation,
  } = useFeedOpsOrderMutations({
    slug, surface, offering, refreshQueue,
    closePlacement: () => setPlacementItem(null),
    clearAllStates: () => setApplyOrderToAllStates(false),
    clearOverviewDraft: () => setOverviewDraft([]),
    formatBulkReason: bulkTargetReasonMessage,
  })

  const deletePostMutation = useMutation({
    mutationFn: (item) => postApi.delete(item.organization_slug || slug, item.id),
    onSuccess: (_result, item) => {
      toast.success(`Marked for deletion — it will be erased after the retention window. Undo from ${pendingDeleteLocation}`, {
        action: { label: 'Undo', onClick: () => undoPendingDeleteMutation.mutate(item) },
      })
      refreshQueue()
      focusQueue()
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to mark post for deletion'),
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: async ({ postIds }) => {
      let marked = 0
      await runInChunks(postIds, async (chunk) => {
        const result = await postApi.deleteMany(slug, chunk)
        marked += result?.pending_delete ?? chunk.length
      })
      return { pending_delete: marked }
    },
    onSuccess: (result, { postIds, unitCount }) => {
      const marked = unitCount ?? result?.pending_delete ?? postIds.length
      toast.success(`Marked ${marked} ${marked === 1 ? 'post' : 'posts'} for deletion — undo from ${pendingDeleteLocation}`, {
        action: { label: 'Undo', onClick: () => undoFamilyDeleteMutation.mutate({ postIds }) },
      })
      setSelectedPostIds((currentValue) => currentValue.filter((postId) => !postIds.includes(postId)))
      pruneProcessedFamilies(postIds)
      refreshQueue()
      focusQueue()
    },
    onError: (error, variables) => reportChunkedFailure(
      error,
      error.response?.data?.error || 'Failed to mark selected posts for deletion',
      variables,
      'marked for deletion',
    ),
  })

  const undoPendingDeleteMutation = useMutation({
    // A distributed (grouped) pending row has every destination marked
    // together; the org-scoped single-post undo would clear only the
    // representative member and strand the rest. Route grouped rows through
    // the group undo so all marked members are restored in one
    // revision-guarded transaction, and keep standalone rows on the
    // single-post endpoint.
    mutationFn: (item) => (item.post_group_id
      ? commandCenterApi.undoPendingDeletePostGroup(item.post_group_id, item.post_group_revision)
      : postApi.undoPendingDelete(item.organization_slug || slug, item.id)),
    onSuccess: () => {
      toast.success('Deletion undone — the post is restored to its previous state')
      refreshQueue()
      focusQueue()
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to undo deletion'),
  })

  const forceDeleteMutation = useMutation({
    mutationFn: (item) => postApi.forceDelete(item.organization_slug || slug, item.id),
    onSuccess: () => {
      toast.success('Post erased permanently')
      refreshQueue()
      focusQueue()
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to erase post'),
  })

  const forceDeleteManyMutation = useMutation({
    // targets: [{ postId, orgSlug }]. Command Center selections span books,
    // so each book's posts go to its own batch-force-delete, chunked.
    mutationFn: async ({ targets }) => {
      let erased = 0
      await runBatches(batchTargetsByOrg(targets), async (batch) => {
        const result = await postApi.forceDeleteMany(batch.orgSlug, batch.ids)
        erased += result?.deleted ?? batch.ids.length
      }, (batch) => batch.ids)
      return { deleted: erased }
    },
    onSuccess: (result, { targets, unitCount }) => {
      const postIds = targets.map((target) => target.postId)
      const erased = unitCount ?? result?.deleted ?? postIds.length
      toast.success(`Erased ${erased} ${erased === 1 ? 'post' : 'posts'} permanently`)
      setSelectedPostIds((currentValue) => currentValue.filter((postId) => !postIds.includes(postId)))
      pruneProcessedFamilies(postIds)
      refreshQueue()
      focusQueue()
    },
    onError: (error, { targets = [] } = {}) => reportChunkedFailure(
      error,
      error.response?.data?.error || 'Failed to erase selected posts',
      { postIds: targets.map((target) => target.postId) },
      'erased',
    ),
  })

  // There is no batch undo endpoint, so an overview family's standalone
  // pending members are undone one by one; a partial failure refreshes the
  // list so the operator sees which members came back.
  const undoFamilyDeleteMutation = useMutation({
    mutationFn: async ({ postIds }) => {
      await runInChunks(postIds, ([postId]) => postApi.undoPendingDelete(slug, postId), 1)
    },
    onSuccess: (_result, { postIds }) => {
      toast.success('Deletion undone — the post is restored to its previous state')
      pruneProcessedFamilies(postIds)
      refreshQueue()
      focusQueue()
    },
    onError: (error, variables) => reportChunkedFailure(
      error,
      error.response?.data?.error || 'Failed to undo deletion',
      variables,
      'restored',
    ),
  })

  // Copy belongs to the post, so a shared save fans out to every regulation the
  // family covers; regulations that already have their own copy are excluded
  // server-side. Saving one regulation forks it first when it is still on the
  // shared copy, so an operator never has to create an empty variant before
  // writing to it.
  const savePostMutation = useMutation({
    mutationFn: async ({ family, patch, scope }) => {
      const rootPostId = familyRootId(family)
      const targetOrganizationSlug = commandCenter
        ? family?.representative?.organization_slug
        : slug
      if (!targetOrganizationSlug) {
        throw new Error('An exact organization is required to edit this post')
      }
      if (!scope) {
        return feedOpsApi.updatePost(targetOrganizationSlug, rootPostId, {
          ...patch,
          scope: isOverview ? 'family' : 'post',
        })
      }
      const variant = (family?.variants || []).find((entry) => entry.offering_id === scope)
      let targetPostId = variant?.post_id
      // A root cannot fork its own last offering: the server refuses to leave a
      // post serving none. Once every other regulation has forked away, the root
      // *is* this regulation's copy, so write to it directly instead of asking
      // for a fork that can never be granted.
      const rootServesOnlyThisScope = variant?.post_id === rootPostId
        && (variant?.target_offerings || []).length === 1
      if (!variant?.is_variant && !rootServesOnlyThisScope) {
        const created = await feedOpsApi.createVariants(targetOrganizationSlug, rootPostId, {
          offering_ids: [scope],
        })
        targetPostId = created?.variants?.[0]?.post_id || targetPostId
      }
      return feedOpsApi.updatePost(targetOrganizationSlug, targetPostId, { ...patch, scope: 'post' })
    },
    onSuccess: (data, variables) => {
      // A save moderation holds is not live: say so, and where the post went
      // when the live list no longer shows it.
      const notice = moderationEditNotice(data)
      if (notice) {
        const notify = notice.tone === 'error' ? toast.error : toast.info
        const whereNow = bookState === 'live' && data?.moderation?.hidden ? ' It now shows under Held by moderation.' : ''
        notify(`${notice.message}${whereNow}`)
      } else {
        toast.success(variables.scope ? 'Saved for this regulation' : 'Post saved')
      }
      setInspectorRowId(null)
      setInspectorScope('')
      refreshQueue()
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to save the post'),
  })

  const regulationSettingsMutation = useMutation({
    mutationFn: ({ variant, changes }) => {
      if (!inspectorOrganizationSlug) {
        throw new Error('An exact organization is required to update this regulation')
      }
      return feedOpsApi.updateRegulationSettings(inspectorOrganizationSlug, variant.post_id, {
        surface,
        offering_id: variant.offering_id,
        ...changes,
      })
    },
    onSuccess: () => refreshQueue(),
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to update the regulation'),
  })

  const resetVariantMutation = useMutation({
    mutationFn: (variant) => {
      if (!inspectorOrganizationSlug) {
        throw new Error('An exact organization is required to reset this regulation')
      }
      return feedOpsApi.resetVariant(inspectorOrganizationSlug, variant.post_id)
    },
    onSuccess: () => {
      toast.success('Reset to the shared copy')
      refreshQueue()
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to update the regulation copy'),
  })

  const providers = useMemo(() => providersQuery.data?.providers || [], [providersQuery.data?.providers])
  const organizations = useMemo(
    () => organizationsQuery.data?.organizations || [],
    [organizationsQuery.data?.organizations],
  )

  // Selection and bulk commands are provider-scoped by contract, but most
  // deployments run exactly one provider — make that case work without a
  // hunt for the Provider filter by scoping to it once on load. Applied a
  // single time so an operator's explicit return to "All providers" sticks.
  const providerAutoScopedRef = useRef(false)
  useEffect(() => {
    if (!commandCenter || providerAutoScopedRef.current || !providersQuery.isFetched) return
    providerAutoScopedRef.current = true
    if (!providerId && providers.length === 1) {
      setPage(1)
      setProviderId(providers[0].id)
    }
  }, [commandCenter, providerId, providers, providersQuery.isFetched])

  // --- Command Center provider-scoped bulk commands -----------------------
  // Every selected post's book can carry its own regulation catalog, so the
  // exact-target expansion (buildBulkTargets) and per-target rejection
  // labels both need a book -> regulation-id list and a regulation-id ->
  // label lookup built across every loaded book, not just the one currently
  // filtered.
  const orgOfferingsById = useMemo(() => new Map(
    organizations.map((organization) => [
      organization.id,
      organization.offering_ids || organization.kambi_offering_ids || [],
    ]),
  ), [organizations])
  const regulationLabelById = useMemo(() => {
    const labels = new Map()
    organizations.forEach((organization) => {
      organizationRegulationMappings(organization).forEach((mapping) => {
        if (mapping.regulation_name && !labels.has(mapping.offering_id)) {
          labels.set(mapping.offering_id, formatRegulationName(mapping.regulation_name))
        }
      })
    })
    return labels
  }, [organizations])

  // Partitions the selection for each Command Center bulk action. Standalone
  // rows (and, with an exact regulation selected, distributed members too —
  // the command writes a per-offering operational override the server allows
  // for grouped members) go through the provider-scoped envelope with exact
  // per-member targets. Without a regulation filter, a distributed post's
  // lifecycle changes as one unit through its group's archive/restore
  // endpoints; deletion and post-type changes stay in the Distributed post
  // editor, so grouped rows are excluded from those actions. Ordering
  // (move to top) is always a per-regulation override, so grouped rows stay
  // on the envelope path for it.
  const bulkSelection = useMemo(() => {
    const empty = { rows: [], groupRows: [], count: 0 }
    if (!commandCenter) {
      return {
        archive: empty, restore: empty, moveToTop: empty, deleteDrafts: empty, promote: empty, demote: empty,
      }
    }
    const partition = ({ memberFilter, groupPredicate = null, groupedRowsOnEnvelope = false, excludeGeneratedParlays = false }) => {
      const rows = []
      const groupRows = []
      selectedPosts.forEach((item) => {
        if (excludeGeneratedParlays && isGeneratedParlay(item)) return
        if (!offering && item.post_group_id && !groupedRowsOnEnvelope) {
          if (groupPredicate && item.post_group_revision != null && groupPredicate(item)) {
            groupRows.push(item)
          }
          return
        }
        if (rowMembers(item).some(memberFilter)) rows.push(item)
      })
      return { rows, groupRows, count: rows.length + groupRows.length }
    }
    return {
      archive: partition({
        memberFilter: BULK_MEMBER_FILTERS.archive,
        groupPredicate: (item) => item.lifecycle === 'active' && !item.is_hidden,
      }),
      restore: partition({
        memberFilter: offering ? BULK_MEMBER_FILTERS.offeringRestore : BULK_MEMBER_FILTERS.restore,
        groupPredicate: (item) => item.is_hidden && item.lifecycle === 'archived_manual',
        excludeGeneratedParlays: true,
      }),
      moveToTop: partition({
        memberFilter: BULK_MEMBER_FILTERS.moveToTop,
        groupedRowsOnEnvelope: true,
        excludeGeneratedParlays: true,
      }),
      deleteDrafts: partition({
        memberFilter: BULK_MEMBER_FILTERS.deleteDrafts,
      }),
      promote: partition({
        memberFilter: BULK_MEMBER_FILTERS.promote,
        excludeGeneratedParlays: true,
      }),
      demote: partition({
        memberFilter: BULK_MEMBER_FILTERS.demote,
        excludeGeneratedParlays: true,
      }),
    }
  }, [commandCenter, offering, selectedPosts])
  const bulkGroupedSelectedCount = commandCenter
    ? selectedPosts.filter((item) => item.post_group_id).length
    : 0
  const bulkScopeSummary = commandCenter
    ? bulkSelectionScopeSummary(selectedPosts, { offering, orgOfferingsById })
    : ''

  // Preserves the operator's viewport across an immediate move-to-top
  // mutation + refetch: capture the next row that will not move (and its
  // on-screen offset) before mutating, then — once the refetched queueDraft
  // re-renders — restore that same visual offset and hand focus back to it,
  // synchronously before paint so there is no top-of-queue flash. No
  // timers/intervals are involved anywhere in this path.
  const captureBulkMoveAnchor = (movedPostIds) => {
    const container = queueFocusRef.current
    if (!container) return
    // A previous bottom-of-list move may have left compensation space so the
    // stable row could remain at the same viewport coordinate. Start each
    // new capture from the queue's natural geometry.
    container.style.removeProperty('--wf-bulk-move-scroll-compensation')
    const movedSet = new Set(movedPostIds)
    const containerRect = container.getBoundingClientRect()
    const movedRows = movedPostIds
      .map((postId) => container.querySelector(`[data-testid="post-row-${postId}"]`))
      .filter(Boolean)
    const movedTop = movedRows.length
      ? Math.min(...movedRows.map((row) => row.getBoundingClientRect().top))
      : containerRect.top
    const stableRows = queueDraft
      .filter((item) => !movedSet.has(item.id))
      .map((item) => ({
        item,
        element: container.querySelector(`[data-testid="post-row-${item.id}"]`),
      }))
      .filter(({ element }) => {
        if (!element) return false
        const rect = element.getBoundingClientRect()
        return rect.bottom > containerRect.top && rect.top < containerRect.bottom
      })
    const anchor = stableRows.reduce((closest, candidate) => {
      if (!closest) return candidate
      const candidateDistance = Math.abs(candidate.element.getBoundingClientRect().top - movedTop)
      const closestDistance = Math.abs(closest.element.getBoundingClientRect().top - movedTop)
      return candidateDistance < closestDistance ? candidate : closest
    }, null)
    if (!anchor) return
    bulkMoveAnchorRef.current = {
      postId: anchor.item.id,
      viewportTop: anchor.element.getBoundingClientRect().top,
    }
  }
  useLayoutEffect(() => {
    const anchor = bulkMoveAnchorRef.current
    if (!anchor) return
    const container = queueFocusRef.current
    if (!container) return
    const rowEl = container.querySelector(`[data-testid="post-row-${anchor.postId}"]`)
    if (!rowEl) return
    bulkMoveAnchorRef.current = null
    const delta = rowEl.getBoundingClientRect().top - anchor.viewportTop
    if (delta > 0) {
      const availableScroll = Math.max(
        0,
        container.scrollHeight - container.clientHeight - container.scrollTop,
      )
      const missingScroll = Math.max(0, Math.ceil(delta - availableScroll))
      if (missingScroll) {
        // At the bottom of the queue there is no remaining scroll range to
        // absorb a row moved above the anchor. Retain exactly the missing
        // range as bottom breathing room; it clears when queue scope changes
        // or before the next move.
        container.style.setProperty('--wf-bulk-move-scroll-compensation', `${missingScroll}px`)
      }
    }
    if (delta) container.scrollTop += delta
    rowEl.focus({ preventScroll: true })
  }, [queueDraft])
  useEffect(() => {
    setBulkCommandResult(null)
    setApplyOrderToAllStates(false)
    queueFocusRef.current?.style.removeProperty('--wf-bulk-move-scroll-compensation')
  }, [queueRequestKey])

  // One mutation covers both write paths of a Command Center bulk action.
  // The provider-scoped envelope runs first: its validation is
  // all-or-nothing over every exact target, so a rejected envelope stops
  // the action before any distributed post is touched. Each distributed
  // post then archives/restores through its own group endpoint — one atomic
  // server-side transaction per group, revision-guarded, so a few run at
  // once — and per-group failures surface in the result panel, in selection
  // order, without discarding the rest.
  const bulkCommandMutation = useMutation({
    mutationFn: async (variables) => {
      const result = variables.envelope
        ? await commandCenterApi.createBulkCommand(variables.envelope)
        : { results: [] }
      const groupOutcomes = await mapWithConcurrency(variables.groups, GROUP_LIFECYCLE_CONCURRENCY, async (group) => {
        try {
          if (variables.meta.kind === 'archive') {
            await commandCenterApi.archivePostGroup(group.id, group.revision, 'Archived by operator')
          } else {
            await commandCenterApi.restorePostGroup(group.id, group.revision)
          }
          return { group, ok: true }
        } catch (error) {
          return {
            group,
            ok: false,
            message: error.response?.data?.message || error.response?.data?.error || 'The distributed post was not changed.',
          }
        }
      })
      return { result, groupOutcomes }
    },
    onSuccess: ({ result, groupOutcomes }, variables) => {
      const succeededIds = new Set((result.results || []).map((entry) => entry.post_id))
      groupOutcomes.filter((outcome) => outcome.ok).forEach((outcome) => {
        outcome.group.memberPostIds.forEach((postId) => succeededIds.add(postId))
      })
      // A row leaves the selection once any of its members changed; the
      // refetch right after this re-derives what is still selectable.
      setSelectedPostIds((currentValue) => currentValue.filter((postId) => {
        const row = queueDraft.find((item) => item.id === postId)
        const memberIds = row ? rowMembers(row).map((member) => member.post_id) : [postId]
        return !memberIds.some((memberId) => succeededIds.has(memberId))
      }))
      const failedGroups = groupOutcomes.filter((outcome) => !outcome.ok)
      const succeededGroupCount = groupOutcomes.length - failedGroups.length
      const resultCount = (result.results || []).length
        + groupOutcomes.filter((outcome) => outcome.ok)
          .reduce((total, outcome) => total + outcome.group.memberPostIds.length, 0)
      setBulkCommandResult({
        actionLabel: variables.meta.actionLabel,
        scopeSummary: variables.meta.scopeSummary,
        resultCount,
        successHeader: variables.meta.copy?.resultHeader,
        successBody: variables.meta.copy?.resultBody?.(resultCount),
        reasons: failedGroups.length
          ? failedGroups.map((outcome) => ({
            index: -1,
            code: 'group_failed',
            message: `“${outcome.group.label}”: ${outcome.message}`,
          }))
          : undefined,
      })
      if (variables.meta.kind === 'set_visible_item_limit') {
        setBulkVisibleItemLimitInput('')
        queryClient.invalidateQueries({ queryKey: ['feed-ops-surfaces', activeOrganizationSlug] })
      }
      if (failedGroups.length) {
        toast.error(`${variables.meta.actionLabel} finished with ${failedGroups.length} distributed ${failedGroups.length === 1 ? 'post' : 'posts'} needing attention`)
      } else {
        toast.success(variables.meta.copy?.resultBody
          ? variables.meta.copy.resultBody(resultCount)
          : `${variables.meta.actionLabel} complete for ${resultCount} ${resultCount === 1 ? 'post' : 'posts'}${succeededGroupCount ? ` (${succeededGroupCount} distributed)` : ''}`)
      }
      if (variables.meta.kind !== 'move_to_top') focusQueue()
      refreshQueue()
    },
    onError: (error, variables) => {
      if (variables.meta.kind === 'move_to_top') bulkMoveAnchorRef.current = null
      const reasons = error.response?.data?.reasons
      if (Array.isArray(reasons) && reasons.length) {
        setBulkCommandResult({
          actionLabel: variables.meta.actionLabel,
          scopeSummary: variables.meta.scopeSummary,
          reasons: reasons.map((reason) => ({ ...reason, target: variables.meta.targets[reason.index] })),
        })
        toast.error(`${variables.meta.actionLabel} could not run — ${reasons.length} ${reasons.length === 1 ? 'target needs' : 'targets need'} attention`)
        return
      }
      toast.error(error.response?.data?.error || `Failed to ${variables.meta.actionLabel.toLowerCase()}`)
    },
  })
  const bulkCommandPendingKind = bulkCommandMutation.isPending ? bulkCommandMutation.variables?.meta?.kind : null
  const dismissBulkCommandResult = () => setBulkCommandResult(null)

  // Every Command Center bulk action funnels through here so the envelope
  // (exact targets, never a blank "all" regulation/surface), the whole-group
  // lifecycle calls, the scope confirmation, and the in-place result all
  // stay in one place.
  const runBulkCommand = ({ action, kind, actionLabel, posts, groupRows = [], parameters = {}, destructive = false, dedupeByPost = false, memberFilter, copy }) => {
    const targets = buildBulkTargets(posts, { surface, offering, orgOfferingsById, dedupeByPost, memberFilter })
    const groups = groupRows.map((row) => ({
      id: row.post_group_id,
      revision: row.post_group_revision,
      label: row.post_group_label || row.title || row.text || 'Distributed post',
      memberPostIds: rowMembers(row).map((member) => member.post_id),
    }))
    if (!targets.length && !groups.length) {
      toast.error('No eligible regulations were found for the selected posts')
      return
    }
    const affectedCount = posts.length + groupRows.length
    const scopeSummary = bulkSelectionScopeSummary([...posts, ...groupRows], { offering, orgOfferingsById, memberFilter })
    const providerName = providers.find((provider) => provider.id === providerId)?.name || 'this provider'
    const expandsRegulations = targets.length > posts.length
    const execute = () => {
      setConfirmAction(null)
      setBulkCommandResult(null)
      bulkCommandMutation.mutate({
        envelope: targets.length ? {
          idempotency_key: crypto.randomUUID(),
          provider_id: providerId,
          action,
          targets,
          parameters,
        } : null,
        groups,
        meta: { kind, actionLabel, scopeSummary, targets, copy },
      })
    }
    if (destructive || expandsRegulations || groups.length) {
      setConfirmAction({
        title: copy?.confirmTitle ? copy.confirmTitle(affectedCount) : `${actionLabel} ${affectedCount} ${affectedCount === 1 ? 'post' : 'posts'}?`,
        message: `Applies to ${scopeSummary} on ${providerName}.${groups.length ? ` Includes ${groups.length} distributed ${groups.length === 1 ? 'post' : 'posts'}, which ${groups.length === 1 ? 'changes' : 'change'} in every book at once.` : ''}${destructive ? ` ${copy?.confirmNote ?? 'This affects the live feed immediately.'}` : ''}`,
        confirmLabel: copy?.confirmLabel ?? actionLabel,
        variant: destructive ? 'danger' : 'default',
        onConfirm: execute,
      })
      return
    }
    execute()
  }

  const bulkArchiveSelectedCommandCenter = () => {
    if (!bulkSelection.archive.count) return
    runBulkCommand({
      action: offering ? 'set_visibility' : 'archive',
      kind: 'archive',
      actionLabel: 'Archive',
      posts: bulkSelection.archive.rows,
      groupRows: bulkSelection.archive.groupRows,
      parameters: offering ? { is_hidden: true } : {},
      memberFilter: BULK_MEMBER_FILTERS.archive,
      destructive: true,
    })
  }
  const bulkRestoreSelectedCommandCenter = () => {
    if (!bulkSelection.restore.count) return
    runBulkCommand({
      action: offering ? 'set_visibility' : 'restore',
      kind: 'restore',
      actionLabel: 'Restore',
      posts: bulkSelection.restore.rows,
      groupRows: bulkSelection.restore.groupRows,
      parameters: offering ? { is_hidden: false } : {},
      memberFilter: offering ? BULK_MEMBER_FILTERS.offeringRestore : BULK_MEMBER_FILTERS.restore,
    })
  }
  const bulkMoveToTopSelectedCommandCenter = () => {
    if (!bulkSelection.moveToTop.rows.length) return
    captureBulkMoveAnchor(bulkSelection.moveToTop.rows.map((item) => item.id))
    runBulkCommand({
      action: 'move_to_top',
      kind: 'move_to_top',
      actionLabel: 'Move to top',
      posts: bulkSelection.moveToTop.rows,
      memberFilter: BULK_MEMBER_FILTERS.moveToTop,
    })
  }
  const bulkDeleteSelectedCommandCenter = () => {
    if (!bulkSelection.deleteDrafts.rows.length) return
    runBulkCommand({
      action: 'delete_drafts',
      kind: 'delete_drafts',
      actionLabel: 'Mark for deletion',
      posts: bulkSelection.deleteDrafts.rows,
      memberFilter: BULK_MEMBER_FILTERS.deleteDrafts,
      destructive: true,
      dedupeByPost: true,
      // delete_drafts now marks drafts for later erasure instead of removing
      // them, so the confirm and result copy must say the posts are hidden and
      // recoverable — not gone — until the retention sweep erases them.
      copy: {
        confirmTitle: (count) => `Mark ${count} ${count === 1 ? 'draft' : 'drafts'} for deletion?`,
        confirmNote: 'Marked drafts are hidden now and stay recoverable from Pending deletion until they are erased.',
        confirmLabel: 'Mark for deletion',
        resultHeader: 'Drafts marked for deletion',
        resultBody: (count) => `Marked ${count} ${count === 1 ? 'draft' : 'drafts'} for deletion — recoverable until erased`,
      },
    })
  }
  // "Promote" marks a post as a Promotion: the widget badges it and the feed
  // ranks it at the top (9999 effective score). post_type is post-global, so
  // the envelope dedupes to one target per post.
  const bulkPromoteSelectedCommandCenter = () => {
    if (!bulkSelection.promote.rows.length) return
    runBulkCommand({
      action: 'set_post_type',
      kind: 'promote',
      actionLabel: 'Promote',
      posts: bulkSelection.promote.rows,
      parameters: { post_type: 'promotion' },
      memberFilter: BULK_MEMBER_FILTERS.promote,
      dedupeByPost: true,
    })
  }
  const bulkDemoteSelectedCommandCenter = () => {
    if (!bulkSelection.demote.rows.length) return
    runBulkCommand({
      action: 'set_post_type',
      kind: 'demote',
      actionLabel: 'Remove promotion',
      posts: bulkSelection.demote.rows,
      parameters: { post_type: 'organic' },
      memberFilter: BULK_MEMBER_FILTERS.demote,
      dedupeByPost: true,
    })
  }
  const bulkSetVisibleItemLimitCommandCenter = () => {
    const parsed = Number(bulkVisibleItemLimitInput)
    if (!selectedPosts.length || !Number.isInteger(parsed) || parsed < 1 || parsed > 50) {
      toast.error('Enter a whole number between 1 and 50')
      return
    }
    runBulkCommand({
      action: 'set_visible_item_limit',
      kind: 'set_visible_item_limit',
      actionLabel: 'Change visible limit',
      posts: selectedPosts,
      parameters: { visible_item_limit: parsed },
    })
  }

  const availableTags = useMemo(() => tagsQuery.data?.tags || [], [tagsQuery.data?.tags])
  // Populated only by the central endpoint (ListCommandCenterQueue); the
  // per-org dashboard has no lifecycle tabs to feed.
  const lifecycleCounts = commandCenter ? queueQuery.data?.lifecycle_counts || null : null
  const queueTotal = queueQuery.data?.total ?? queueDraft.length
  const queueLimit = queueQuery.data?.limit || QUEUE_PAGE_SIZE
  const queueOffset = queueQuery.data?.offset ?? (page - 1) * queueLimit
  const listLength = isOverview ? overviewItems.length : queueDraft.length
  const listTotal = isOverview ? (overviewQuery.data?.total ?? overviewItems.length) : queueTotal
  const listLimit = isOverview ? (overviewQuery.data?.limit || QUEUE_PAGE_SIZE) : queueLimit
  const listOffset = isOverview
    ? (overviewQuery.data?.offset ?? (page - 1) * listLimit)
    : queueOffset
  const queuePageCount = Math.max(1, Math.ceil(listTotal / listLimit))
  const hasNarrowingFilters = Boolean(
    searchAndCreator.search
    || searchAndCreator.filter
    || status
    || visibility
    || bookState !== 'live'
    || moderationFilter
    || source !== 'creator'
    || contentType
    || parlayStatus
    || parlayOrigin
    || tagId
    || (commandCenter && lifecycle !== 'active')
    || (commandCenter && includePersonalized),
  )
  // Central reordering writes absolute manual ranks for one exact
  // (book, surface, regulation) feed, so it stays withheld until the
  // operator has named all three — a rank saved against "all books" or
  // "all regulations" would not map onto any real feed position. A
  // narrowing filter hides rows between the visible ones, which has the
  // same effect, so it withholds reordering too.
  //
  // Pages after the first are withheld until the manually ranked block
  // already reaches them: ranks are absolute (queueOffset + index + 1), and
  // a manually placed row always sorts above an automatic one, so ranking an
  // automatically ordered page 2 would make its rows leapfrog page 1. The
  // first row of the page carrying a manual placement proves the block
  // reaches this far, because manual rows are never interleaved below
  // automatic ones.
  const commandCenterOrderPageIsRankable = queueOffset === 0 || hasManualPlacement(queueDraft[0])
  const canOrderCommandCenterQueue = commandCenter
    && canBulkCommand
    && Boolean(organizationId)
    && Boolean(offering)
    && commandCenterOrderPageIsRankable
  const canOrderPosts = commandCenter
    ? canOrderCommandCenterQueue && !hasNarrowingFilters
    : (canPublishPosts || canOperatePosts)
  // A cutoff line is truthful only when it is backed by one exact book,
  // surface, and regulation: buildFeedOpsQueuePredicates's book_state=live
  // join resolves one physical feed_surfaces row per organization (by
  // `fs.slug = surface`) in both the per-org dashboard and Command Center,
  // so 'default-feed'/'widget' names a real per-book slug, not an abstract
  // bucket, once one organization is selected. Personalized content is
  // per-viewer and has no single universal rank, so includePersonalized
  // must stay off
  // (already covered above) and every source's own book must be exact.
  const hasExactRegulationScope = offeringOptions.length === 0 || Boolean(offering)
  const hasExactBookScope = commandCenter ? Boolean(organizationId) : true
  const activeSurface = (surfacesQuery.data?.items || []).find((item) => item.slug === surface)
  const surfaceVisibleItemLimit = activeSurface
    ? resolveVisibleItemLimit(activeSurface.config, activeSurface.display_type)
    : null
  const showVisibilityCutoff = !isOverview
    && hasExactBookScope
    && hasExactRegulationScope
    && !hasNarrowingFilters
    && surfaceVisibleItemLimit !== null
    && queueTotal > surfaceVisibleItemLimit
  const visibilityCutoffBelowCount = showVisibilityCutoff ? queueTotal - surfaceVisibleItemLimit : 0
  // A filtered overview hides families between the visible ones, so its positions
  // no longer map onto real feed positions and a save would invent an order.
  const canOrderOverview = canOrderPosts && !hasNarrowingFilters
  const canInteractWithOverviewOrder = canOrderOverview && !overviewMoveToTopPending
  // Overview drags write one rank sequence per regulation (upsertOverviewOrder),
  // so an operator who has not narrowed to a regulation is ordering every
  // regulation at once. Say so while the draft is unsaved; with one
  // regulation the two scopes are the same feed and there is nothing to warn
  // about.
  const showOverviewReorderWarning = isOverview
    && canOrderOverview
    && isOverviewOrderDirty
    && offeringOptions.length > 1
  // Column width for the reorder cluster is reserved from the number of
  // controls the list can render, not from what a given row happens to show,
  // so suppressed rows and the header keep the same template.
  const overviewOrderSlots = canOrderOverview ? 3 : 0
  const queueOrderSlots = canOrderPosts ? 3 : 0
  const isCanonicalCurrentCreatorScope = (
    bookState === 'live'
    && source === 'creator'
    && surface === defaultSurface
    && (!commandCenter || !offering)
    && !providerId
    && !organizationId
  )
  const canReplaceExistingOrder = !commandCenter
    && !isOverview
    && isCanonicalCurrentCreatorScope
    && !hasNarrowingFilters
    && page === 1
    && queueDraft.length === queueTotal
  const isOrderDirty = !isOverview && compareOrder(queueDraft, queueQuery.data?.items || [])
  const holdsUnsavedOrder = (queueContextMatches && isOrderDirty) || isOverviewOrderDirty
  useEffect(() => {
    setModerationPollPaused(holdsUnsavedOrder)
  }, [holdsUnsavedOrder])
  const placementIndex = placementItem
    ? queueDraft.findIndex((item) => item.id === placementItem.id)
    : -1
  const activeFilters = useMemo(() => {
    const filters = []
    if (status) {
      filters.push({
        key: 'status',
        label: `Approval: ${STATUS_OPTIONS.find((option) => option.value === status)?.label || status}`,
      })
    }
    if (!archivedOnly && visibility) {
      filters.push({
        key: 'visibility',
        label: `Visibility: ${VISIBILITY_OPTIONS.find((option) => option.value === visibility)?.label || visibility}`,
      })
    }
    if (bookState !== 'live' && visibility !== 'hidden') {
      filters.push({
        key: 'bookState',
        label: commandCenter
          ? 'Includes posts not in the live feed'
          : `Book status: ${BOOK_STATE_OPTIONS.find((option) => option.value === bookState)?.label || bookState}`,
      })
    }
    if (commandCenter && surface !== DEFAULT_SURFACE_SLUG) {
      filters.push({
        key: 'surface',
        label: `Surface: ${surfaceOptions.find((option) => option.value === surface)?.label || surface}`,
      })
    }
    if (commandCenter && includePersonalized) {
      filters.push({ key: 'personalized', label: 'Personalized bets included' })
    }
    if (!commandCenter && moderationFilter) {
      filters.push({ key: 'moderation', label: 'Held by moderation' })
    }
    if (source !== 'creator') {
      filters.push({
        key: 'source',
        label: `Source: ${SOURCE_OPTIONS.find((option) => option.value === source)?.label || source}`,
      })
    }
    if (contentType) {
      filters.push({
        key: 'contentType',
        label: `Content: ${CONTENT_TYPE_OPTIONS.find((option) => option.value === contentType)?.label || contentType}`,
      })
    }
    if (contentType === 'parlay' && parlayStatus) {
      filters.push({
        key: 'parlayStatus',
        label: `Parlay status: ${PARLAY_STATUS_OPTIONS.find((option) => option.value === parlayStatus)?.label || parlayStatus}`,
      })
    }
    if (contentType === 'parlay' && parlayOrigin) {
      filters.push({
        key: 'parlayOrigin',
        label: `Parlay origin: ${PARLAY_ORIGIN_OPTIONS.find((option) => option.value === parlayOrigin)?.label || parlayOrigin}`,
      })
    }
    if (tagId) {
      filters.push({
        key: 'tag',
        label: `Tag: ${availableTags.find((tag) => tag.id === tagId)?.label || tagId}`,
      })
    }
    if (!commandCenter && searchAndCreator.filter) {
      filters.push({ key: 'creator', label: `Creator: ${searchAndCreator.filter}` })
    }
    return filters
  }, [
    archivedOnly,
    availableTags,
    bookState,
    includePersonalized,
    surface,
    surfaceOptions,
    commandCenter,
    contentType,
    moderationFilter,
    parlayOrigin,
    parlayStatus,
    searchAndCreator.filter,
    source,
    status,
    tagId,
    visibility,
  ])
  const clearAdvancedFilters = () => {
    setPage(1)
    setSearchAndCreator((currentValue) => ({ ...currentValue, filter: '' }))
    setStatus('')
    setVisibility(archivedOnly ? 'hidden' : '')
    setBookState('live')
    setModerationFilter('')
    setSource('creator')
    if (commandCenter) {
      setSurface(DEFAULT_SURFACE_SLUG)
      setIncludePersonalized(false)
    }
    setContentType('')
    setParlayStatus('')
    setParlayOrigin('')
    setTagId('')
  }
  const clearNarrowingFilters = () => {
    clearAdvancedFilters()
    setSearchAndCreator({ search: '', filter: '' })
  }
  const clearSelectedPosts = () => {
    setSelectedPostIds([])
    focusQueue()
  }
  const clearSelectedFamilies = () => {
    setSelectedFamilyKeys([])
    focusQueue()
  }
  const removeAdvancedFilter = (key) => {
    setPage(1)
    if (key === 'status') setStatus('')
    if (key === 'bookState') setBookState('live')
    if (key === 'moderation') setModerationFilter('')
    if (key === 'source') setSource('creator')
    if (key === 'surface') setSurface(DEFAULT_SURFACE_SLUG)
    if (key === 'personalized') setIncludePersonalized(false)
    if (key === 'visibility') setVisibility('')
    if (key === 'contentType') {
      setContentType('')
      setParlayStatus('')
      setParlayOrigin('')
    }
    if (key === 'parlayStatus') setParlayStatus('')
    if (key === 'parlayOrigin') setParlayOrigin('')
    if (key === 'tag') setTagId('')
    if (key === 'creator') {
      setSearchAndCreator((currentValue) => ({ ...currentValue, filter: '' }))
    }
  }
  const hasManualOrder = queueDraft.some((item) => (
    item.override?.manual_rank > 0
    || item.override?.pin_to_top
    || (item.override?.placement_strategy && item.override.placement_strategy !== 'manual')
  ))
  const queueStart = listTotal > 0 ? listOffset + 1 : 0
  const queueEnd = Math.min(listOffset + listLength, listTotal)
  const resultRange = listTotal > 0
    ? `Showing ${queueStart}–${queueEnd} of ${listTotal} posts`
    : hasNarrowingFilters ? 'No matching posts' : 'No posts'
  const orderStateLabel = isOrderDirty
    ? 'Unsaved order'
    : hasManualOrder ? 'Manual order' : 'Automatic order'
  const visibilityActionPending = bulkVisibilityMutation.isPending
    ? (bulkVisibilityMutation.variables?.isHidden ? 'archive' : 'restore')
    : null
  // Two order-save paths share the sticky bar: the per-organization order
  // endpoint and Command Center's set_order bulk command.
  const orderSavePending = commandCenter
    ? saveCommandCenterOrderMutation.isPending
    : saveOrderMutation.isPending
  const commandCenterOrderBookName = commandCenter && organizationId
    ? organizations.find((organization) => organization.id === organizationId)?.name || ''
    : ''
  const restrictedSelectionCount = isOverview ? overviewSelection.protectedCount : protectedSelectedCount
  const deleteRestrictionText = restrictedSelectionCount === 1
    ? 'This post is owned by a source feed, a distributed post, or a generator (archive it instead), or is already marked for deletion.'
    : 'These posts are owned by a source feed, a distributed post, or a generator (archive them instead), or are already marked for deletion.'



  const saveCurrentOrder = () => {
    if (commandCenter) {
      const envelope = buildCommandCenterOrderEnvelope({
        queueDraft,
        queueOffset,
        surface,
        offering,
        applyToAllStates: applyOrderToAllStates,
        orgOfferingsById,
        providerId,
      })
      if (!envelope) {
        toast.error('Generated content cannot be manually ordered')
        return
      }
      if (!applyOrderToAllStates) {
        saveCommandCenterOrderMutation.mutate(envelope)
        return
      }
      const stateCount = new Set(envelope.targets.map((target) => target.offering_id)).size
      setConfirmAction({
        title: 'Apply this order to every state?',
        message: `Saves positions ${queueOffset + 1}–${queueOffset + queueDraft.length} for ${stateCount} ${stateCount === 1 ? 'state' : 'states'} on ${commandCenterOrderBookName || 'this book'} · ${surfaceLabel}. Posts not published to a state keep the order they have there.`,
        confirmLabel: 'Apply order',
        onConfirm: () => {
          setConfirmAction(null)
          saveCommandCenterOrderMutation.mutate(envelope)
        },
      })
      return
    }
    const items = []
    queueDraft.forEach((item, index) => {
      if (isGeneratedQueueItem(item)) return
      items.push({
        post_id: item.id,
        manual_rank: queueOffset + index + 1,
        pin_to_top: Boolean(item.override?.pin_to_top),
        placement_strategy: item.override?.placement_strategy || 'manual',
        boost_factor: item.override?.boost_factor ?? null,
        apply_until: item.override?.apply_until || null,
        notes: item.override?.notes || null,
      })
    })
    if (!items.length) {
      toast.error('Generated content cannot be manually ordered')
      return
    }
    saveOrderMutation.mutate({ items, replaceExisting: canReplaceExistingOrder })
  }
  const discardCurrentOrder = () => {
    setQueueDraft(queueQuery.data?.items || [])
    setRankAnnouncement('Unsaved queue order discarded')
  }

  const saveSelectedOrdering = () => {
    if (!placementItem || placementIndex < 0) return
    const parsedBoost = Number(orderingForm.boost_factor)
    const boostFactor = Number.isFinite(parsedBoost) && parsedBoost > 0
      ? parsedBoost
      : placementItem.manual_priority ?? 1
    const applyUntil = toIsoOrNull(orderingForm.apply_until)
    if (isGeneratedQueueItem(placementItem) && (
      !orderingForm.pin_to_top || !applyUntil || !isFutureDate(applyUntil)
    )) {
      toast.error('Generated pins need a future expiry')
      return
    }

    saveOrderMutation.mutate({
      replaceExisting: false,
      closePlacement: true,
      items: [{
        post_id: placementItem.id,
        manual_rank: queueOffset + placementIndex + 1,
        pin_to_top: orderingForm.pin_to_top,
        placement_strategy: orderingForm.placement_strategy,
        boost_factor: boostFactor,
        apply_until: applyUntil,
        notes: orderingForm.notes || null,
        ...(commandCenter ? { organization_id: placementItem.organization_id } : {}),
      }],
    })
  }

  const handleDrop = (targetId) => {
    if (!draggedId || draggedId === targetId) return

    setQueueDraft((currentValue) => {
      const items = [...currentValue]
      const draggedIndex = items.findIndex((item) => item.id === draggedId)
      const targetIndex = items.findIndex((item) => item.id === targetId)
      if (draggedIndex === -1 || targetIndex === -1) {
        return currentValue
      }

      const [movedItem] = items.splice(draggedIndex, 1)
      items.splice(targetIndex, 0, movedItem)
      setRankAnnouncement(`${movedItem.text || 'Post'} moved to position ${targetIndex + 1}`)
      return items
    })
    setDraggedId(null)
  }

  const movePost = (postId, direction) => {
    setQueueDraft((currentValue) => {
      const fromIndex = currentValue.findIndex((item) => item.id === postId)
      if (fromIndex < 0) return currentValue
      const toIndex = direction === 'top'
        ? 0
        : Math.max(0, Math.min(currentValue.length - 1, fromIndex + (direction === 'up' ? -1 : 1)))
      if (fromIndex === toIndex) return currentValue
      const items = [...currentValue]
      const [movedItem] = items.splice(fromIndex, 1)
      items.splice(toIndex, 0, movedItem)
      setRankAnnouncement(`${movedItem.text || 'Post'} moved to position ${toIndex + 1}`)
      return items
    })
  }

  const reorderOverview = (mutate) => {
    setOverviewDraft((currentValue) => {
      const items = [...(currentValue.length ? currentValue : overviewServerItems)]
      const result = mutate(items)
      return result || currentValue
    })
  }

  const handleOverviewDrop = (targetKey) => {
    if (!overviewDraggedKey || overviewDraggedKey === targetKey) return
    reorderOverview((items) => {
      const fromIndex = items.findIndex((family) => family.family_key === overviewDraggedKey)
      const toIndex = items.findIndex((family) => family.family_key === targetKey)
      if (fromIndex < 0 || toIndex < 0) return null
      const [moved] = items.splice(fromIndex, 1)
      items.splice(toIndex, 0, moved)
      setRankAnnouncement(`${overviewFamilyLabel(moved)} moved to position ${toIndex + 1}`)
      return items
    })
    setOverviewDraggedKey(null)
  }

  const loadOverviewOrderPrefix = async () => {
    const prefixLength = overviewPageOffset + overviewServerItems.length
    const chunkCount = Math.ceil(prefixLength / OVERVIEW_ORDER_FETCH_SIZE)
    const pages = await Promise.all(Array.from({ length: chunkCount }, (_, index) => {
      const cursor = index * OVERVIEW_ORDER_FETCH_SIZE
      return feedOpsApi.listQueue(slug, {
        ...sharedQueueParams,
        limit: Math.min(OVERVIEW_ORDER_FETCH_SIZE, prefixLength - cursor),
        cursor,
        ...(searchAndCreator.filter ? { creator: searchAndCreator.filter } : {}),
        view: 'overview',
      })
    }))
    const items = pages.flatMap((result) => result?.items || [])
    if (items.length < prefixLength) {
      throw new Error('Overview changed while preparing the order')
    }
    items.splice(overviewPageOffset, overviewItems.length, ...overviewItems)
    return items.slice(0, prefixLength)
  }

  const moveOverviewFamily = async (familyKey, direction) => {
    if (overviewMoveToTopPending) return
    if (direction === 'top' && overviewPageOffset > 0 && !isGlobalOverviewDraft) {
      const requestId = ++overviewMoveRequestRef.current
      setOverviewMoveToTopPending(true)
      try {
        const items = await loadOverviewOrderPrefix()
        if (requestId !== overviewMoveRequestRef.current) return
        const fromIndex = items.findIndex((family) => family.family_key === familyKey)
        if (fromIndex < 0) throw new Error('Post is no longer in the overview')
        const [moved] = items.splice(fromIndex, 1)
        items.unshift(moved)
        setOverviewDraft(items)
        setRankAnnouncement(`${overviewFamilyLabel(moved)} moved to position 1`)
      } catch {
        if (requestId === overviewMoveRequestRef.current) {
          toast.error('Could not move this post to the top. Refresh and try again.')
        }
      } finally {
        if (requestId === overviewMoveRequestRef.current) {
          setOverviewMoveToTopPending(false)
        }
      }
      return
    }

    reorderOverview((items) => {
      const fromIndex = items.findIndex((family) => family.family_key === familyKey)
      if (fromIndex < 0) return null
      const toIndex = direction === 'top'
        ? 0
        : Math.max(0, Math.min(items.length - 1, fromIndex + (direction === 'up' ? -1 : 1)))
      if (fromIndex === toIndex) return null
      const [moved] = items.splice(fromIndex, 1)
      items.splice(toIndex, 0, moved)
      setRankAnnouncement(`${overviewFamilyLabel(moved)} moved to position ${overviewOrderOffset + toIndex + 1}`)
      return items
    })
  }

  const saveOverviewOrder = () => {
    const offeringCount = new Set(
      overviewOrderFamilies.flatMap((family) => (family.variants || [])
        .map((variant) => variant.offering_id)
        .filter(Boolean)),
    ).size
    const firstPosition = overviewOrderOffset + 1
    const lastPosition = overviewOrderOffset + overviewOrderFamilies.length
    setConfirmAction({
      title: 'Apply this order everywhere?',
      message: `Saves positions ${firstPosition}\u2013${lastPosition} for ${offeringCount} ${offeringCount === 1 ? 'regulation' : 'regulations'} on ${surfaceOptions.find((option) => option.value === surface)?.label || surface}. Posts outside these positions keep the order they have.`,
      confirmLabel: 'Apply order',
      onConfirm: () => {
        setConfirmAction(null)
        saveOverviewOrderMutation.mutate({
          offset: overviewOrderOffset,
          families: overviewOrderFamilies.map((family) => ({
            family_key: family.family_key,
            variants: (family.variants || [])
              .filter((variant) => variant.offering_id)
              .map((variant) => ({ post_id: variant.post_id, offering_id: variant.offering_id })),
          })),
        })
      },
    })
  }

  const openPlacement = (item) => {
    setPlacementItem(item)
    setOrderingForm({
      pin_to_top: Boolean(item.override?.pin_to_top),
      placement_strategy: item.override?.placement_strategy || 'manual',
      boost_factor: item.override?.boost_factor ?? item.manual_priority ?? 1,
      apply_until: formatDateTimeInput(item.override?.apply_until),
      notes: item.override?.notes || '',
    })
  }

  const openGroupEditor = (groupID) => {
    navigate(`/command-center/post-groups/${groupID}/edit`)
  }

  const openEditor = async (item) => {
    if (!canEditPosts || !canUsePostEditor(item, commandCenterAccess)) return
    if (item.post_group_id) {
      openGroupEditor(item.post_group_id)
      return
    }
    if (commandCenter && !await switchOrganization(item.organization_slug)) return
    navigate(`/dashboard/feed/posts/${item.id}/edit`)
  }

  const confirmDeletePost = (item) => {
    const generatedDraft = isGeneratedParlay(item) && item.is_hidden && !item.is_published
    setConfirmAction({
      title: generatedDraft ? 'Mark generated draft for deletion?' : 'Mark post for deletion?',
      message: `Mark “${item.text || 'Untitled post'}” from ${item.organization_name} for deletion? It will be erased after the retention window. You can undo this from ${pendingDeleteLocation}.`,
      confirmLabel: generatedDraft ? 'Mark draft' : 'Mark for deletion',
      variant: 'danger',
      onConfirm: () => {
        deletePostMutation.mutate(item)
        setConfirmAction(null)
      },
    })
  }

  const confirmUndoPendingDelete = (item) => {
    setConfirmAction({
      title: 'Undo deletion?',
      message: 'Restore this post to its state before it was marked for deletion?',
      confirmLabel: 'Undo deletion',
      onConfirm: () => {
        undoPendingDeleteMutation.mutate(item)
        setConfirmAction(null)
      },
    })
  }

  const confirmForceDelete = (item) => {
    setConfirmAction({
      title: 'Erase post permanently?',
      message: 'This permanently erases the post and its retained content. This cannot be undone.',
      confirmLabel: 'Erase now',
      variant: 'danger',
      onConfirm: () => {
        forceDeleteMutation.mutate(item)
        setConfirmAction(null)
      },
    })
  }

  const runFamilyVisibility = (family, isHidden) => {
    const postIds = familyMemberIds(family, isHidden ? 'archive' : 'restore')
    if (!postIds.length) return
    bulkVisibilityMutation.mutate({ isHidden, postIds })
  }

  const confirmArchiveFamily = (family) => {
    const label = overviewFamilyLabel(family)
    const bookName = currentOrg?.name || 'this book'
    // Only operators who may open Command Center learn that a post runs in
    // other books; client tenants get the standard copy (see canUsePostEditor).
    const message = commandCenterAccess && familyIsDistributed(family)
      ? `Archive “${label}” for ${bookName}? It stays live in every other book that runs it. You can restore it later from Archived posts.`
      : `Archive “${label}” from every regulation in ${bookName}? Other books are not affected. You can restore it later from Archived posts.`
    setConfirmAction({
      title: 'Archive from every regulation?',
      message,
      confirmLabel: 'Archive',
      onConfirm: () => {
        runFamilyVisibility(family, true)
        setConfirmAction(null)
      },
    })
  }

  const confirmDeleteFamily = (family) => {
    const postIds = familyMemberIds(family, 'delete')
    if (!postIds.length) return
    const label = overviewFamilyLabel(family)
    const bookName = currentOrg?.name || 'this book'
    const pendingNote = familyHasPendingDelete(family)
      ? ' Copies already marked for deletion keep their current deadline.'
      : ''
    setConfirmAction({
      title: 'Mark post for deletion?',
      message: `Mark “${label}” from ${bookName} for deletion? It is removed from every regulation this book runs it in and erased after the retention window. Other books are not affected. You can undo this until it is erased.${pendingNote}`,
      confirmLabel: 'Mark for deletion',
      variant: 'danger',
      onConfirm: () => {
        if (inspectorRowId && inspectorRowId === familyRootId(family)) closeInspector()
        bulkDeleteMutation.mutate({ postIds })
        setConfirmAction(null)
      },
    })
  }

  const confirmUndoFamilyDelete = (family) => {
    const postIds = familyMemberIds(family, 'undoDelete')
    if (!postIds.length) return
    // A mixed family (a copy marked from a regulation's queue) undoes only
    // its marked copies; the rest of the post is live and untouched.
    const count = postIds.length
    const message = familyIsPendingDelete(family)
      ? 'Restore this post to its state before it was marked for deletion?'
      : `Undo deletion for the ${count} ${count === 1 ? 'copy' : 'copies'} of “${overviewFamilyLabel(family)}” marked for deletion in ${currentOrg?.name || 'this book'}? The rest of the post is not affected.`
    setConfirmAction({
      title: 'Undo deletion?',
      message,
      confirmLabel: 'Undo deletion',
      onConfirm: () => {
        undoFamilyDeleteMutation.mutate({ postIds })
        setConfirmAction(null)
      },
    })
  }

  const confirmForceDeleteFamily = (family) => {
    const postIds = familyMemberIds(family, 'forceDelete')
    if (!postIds.length) return
    setConfirmAction({
      title: 'Erase post permanently?',
      message: 'This permanently erases the post and its retained content. This cannot be undone.',
      confirmLabel: 'Erase now',
      variant: 'danger',
      onConfirm: () => {
        if (inspectorRowId && inspectorRowId === familyRootId(family)) closeInspector()
        forceDeleteManyMutation.mutate({ targets: postIds.map((postId) => ({ postId, orgSlug: slug })) })
        setConfirmAction(null)
      },
    })
  }

  const confirmDeleteSelected = () => {
    if (!deletableSelectedIds.length) return
    setConfirmAction({
      title: `Mark ${deletableSelectedIds.length} eligible ${deletableSelectedIds.length === 1 ? 'post' : 'posts'} for deletion?`,
      message: `Each post will be erased after the retention window. Undo from ${pendingDeleteLocation}. Source-managed, distributed, and generated posts stay selected for archival instead.`,
      confirmLabel: `Mark ${deletableSelectedIds.length}`,
      variant: 'danger',
      onConfirm: () => {
        bulkDeleteMutation.mutate({ postIds: deletableSelectedIds })
        setConfirmAction(null)
      },
    })
  }

  const confirmArchiveSelectedFamilies = () => {
    const { familyCount: count, postIds } = overviewSelection.archive
    if (!postIds.length) return
    const bookName = currentOrg?.name || 'this book'
    const noun = count === 1 ? 'post' : 'posts'
    const distributed = overviewSelection.distributedCount
    // Only operators who may open Command Center learn that a post runs in
    // other books (see confirmArchiveFamily).
    const distributedNote = commandCenterAccess && distributed > 0
      ? ` ${distributed} of them ${distributed === 1 ? 'runs' : 'run'} in other books and ${distributed === 1 ? 'stays' : 'stay'} live there.`
      : ''
    setConfirmAction({
      title: `Archive ${count} ${noun} from every regulation?`,
      message: `Archive ${count} selected ${noun} across every regulation in ${bookName}? Other books are not affected. You can restore them later from Archived posts.${distributedNote}`,
      confirmLabel: 'Archive',
      onConfirm: () => {
        bulkVisibilityMutation.mutate({ isHidden: true, postIds, unitCount: count })
        setConfirmAction(null)
      },
    })
  }

  const confirmDeleteSelectedFamilies = () => {
    const { familyCount: count, postIds, families } = overviewSelection.delete
    if (!postIds.length) return
    const bookName = currentOrg?.name || 'this book'
    setConfirmAction({
      title: `Mark ${count} eligible ${count === 1 ? 'post' : 'posts'} for deletion?`,
      message: `Each post is removed from every regulation ${bookName} runs it in and erased after the retention window. Other books are not affected. Source-managed, distributed and generated posts, and posts wholly marked for deletion, stay selected.`,
      confirmLabel: `Mark ${count}`,
      variant: 'danger',
      onConfirm: () => {
        if (inspectorRowId && families.some((family) => familyRootId(family) === inspectorRowId)) closeInspector()
        bulkDeleteMutation.mutate({ postIds, unitCount: count })
        setConfirmAction(null)
      },
    })
  }

  const confirmForceDeleteSelected = () => {
    if (!canForceDelete || !eraseTargets.length) return
    const n = eraseTargets.length
    const skipped = selectedPosts.length - n
    // Command Center-only surface, so naming distribution is allowed here.
    const skippedNote = skipped > 0
      ? ` ${skipped} distributed ${skipped === 1 ? 'post is' : 'posts are'} skipped — undo ${skipped === 1 ? 'it' : 'them'} from ${skipped === 1 ? 'its' : 'their'} row.`
      : ''
    setConfirmAction({
      title: `Erase ${n} ${n === 1 ? 'post' : 'posts'} permanently?`,
      message: `This permanently erases every selected post and cannot be undone.${skippedNote}`,
      confirmLabel: `Erase now (${n})`,
      variant: 'danger',
      onConfirm: () => {
        forceDeleteManyMutation.mutate({ targets: eraseTargets })
        setConfirmAction(null)
      },
    })
  }

  const confirmAutomaticOrder = () => {
    setConfirmAction({
      title: 'Return to automatic order?',
      message: `Remove all saved manual ranks for ${selectedOfferingLabel || 'this surface'}? Posts will return to score and boost-based ordering.`,
      confirmLabel: 'Return to automatic',
      onConfirm: () => {
        resetSurfaceOrderMutation.mutate()
        setConfirmAction(null)
      },
    })
  }

  if (!commandCenter && !slug) {
    return (
      <PageLayout size="workspace">
        <PageHeader title={headerTitle} mode="workspace" context={headerContext} />
        <StatePanel state="restricted" title={archivedOnly ? 'Select an organization to see its archived posts' : 'Select an organization to manage its posts'} />
      </PageLayout>
    )
  }

  const placementForm = placementItem ? (
    <div className={styles.formStack}>
      <div className={styles.readOnlyCard}>
        <span>Queue position</span>
        <strong>{placementIndex >= 0 ? queueOffset + placementIndex + 1 : '-'}</strong>
        <span>{placementItem.override ? 'Manual override active' : 'Automatic position'}</span>
      </div>
      <Select
        aria-label="Placement strategy"
        value={orderingForm.placement_strategy}
        onChange={(event) => setOrderingForm((currentValue) => ({
          ...currentValue,
          placement_strategy: event.target.value,
          pin_to_top: event.target.value === 'top' ? true : currentValue.pin_to_top,
        }))}
      >
        <option value="manual">Exact manual order</option>
        <option value="top">Top</option>
        <option value="middle">Middle</option>
        <option value="bottom">Bottom</option>
              <option value="random">Randomized</option>
      </Select>
      <FormField
            label={offering ? 'Offering boost' : 'Default boost'}
        type="number"
        min="0.1"
        max="100"
        step="0.1"
        value={orderingForm.boost_factor}
        onChange={(event) => setOrderingForm((currentValue) => ({ ...currentValue, boost_factor: event.target.value }))}
      />
      <label className={styles.checkboxRow}>
        <input
          type="checkbox"
          checked={orderingForm.pin_to_top}
          onChange={(event) => setOrderingForm((currentValue) => ({ ...currentValue, pin_to_top: event.target.checked }))}
        />
        <span>Pin to top</span>
      </label>
      <FormField
        label="Apply until"
        type="datetime-local"
        value={orderingForm.apply_until}
        onChange={(event) => setOrderingForm((currentValue) => ({ ...currentValue, apply_until: event.target.value }))}
      />
      <FormField
        label="Notes"
        type="textarea"
        rows={4}
        value={orderingForm.notes}
        onChange={(event) => setOrderingForm((currentValue) => ({ ...currentValue, notes: event.target.value }))}
      />
      <div className={styles.actionRow}>
        <Button
          variant="secondary"
          onClick={() => resetOrderMutation.mutate(placementItem)}
          disabled={saveOrderMutation.isPending}
          loading={resetOrderMutation.isPending}
        >
          Reset to algorithm
        </Button>
        <Button
          onClick={saveSelectedOrdering}
          disabled={resetOrderMutation.isPending}
          loading={saveOrderMutation.isPending}
        >
          Save placement
        </Button>
      </div>
    </div>
  ) : null

  const openInspector = (rowId) => {
    generatedPublishMutation.reset()
    setInspectorScope('')
    setInspectorRowId(rowId)
  }
  const closeInspector = () => {
    generatedPublishMutation.reset()
    setInspectorScope('')
    setInspectorRowId(null)
  }
  const showPreviewFor = (previewItem) => (event) => {
    setHoverPreviewItem(previewItem)
    hoverPreview.show(event)
  }
  const hidePreview = () => {
    hoverPreview.hide()
  }

  const overviewMenuItems = (family) => {
    const rootId = familyRootId(family)
    const representative = family?.representative
    if (!rootId || isGeneratedParlay(representative)) return []
    const items = []
    // Same rule as the queue row: a distributed member only has the group
    // editor, and only for operators allowed into Command Center. The single
    // post editor would accept the edit and the update endpoint would reject it.
    const groupId = representative?.post_group_id
    if (canEditPosts && (!groupId || commandCenterAccess)) {
      items.push({
        key: 'edit',
        label: groupId ? 'Edit distributed post' : 'Open full editor',
        icon: <Pencil size={15} aria-hidden="true" />,
        onSelect: () => (groupId ? openGroupEditor(groupId) : navigate(`/dashboard/feed/posts/${rootId}/edit`)),
      })
    }
    // A family marked for deletion mirrors the queue's pending row: only
    // Undo deletion and (internal org admins) Erase now, never archive,
    // restore or delete. Distributed pending members are undone together in
    // Command Center, so they offer neither here.
    if (familyIsPendingDelete(family)) {
      if (canPublishPosts && familyMemberIds(family, 'undoDelete').length) {
        items.push({
          key: 'undo-delete',
          label: 'Undo deletion',
          icon: <ArchiveRestore size={15} aria-hidden="true" />,
          disabled: listInteractionLocked || undoFamilyDeleteMutation.isPending,
          onSelect: () => confirmUndoFamilyDelete(family),
        })
      }
      if (!commandCenter && canForceDelete && familyMemberIds(family, 'forceDelete').length) {
        items.push({
          key: 'force-delete',
          label: 'Erase now',
          icon: <Trash2 size={15} aria-hidden="true" />,
          danger: true,
          disabled: listInteractionLocked || forceDeleteManyMutation.isPending,
          onSelect: () => confirmForceDeleteFamily(family),
        })
      }
      return items
    }
    // Archive/restore/delete act on this book's member posts only. A
    // distributed post can be archived or restored for this book alone; it
    // is never deletable here (its members carry group_managed).
    const visibilityLocked = listInteractionLocked || bulkVisibilityMutation.isPending
    if (canBulkVisibility && familyMemberIds(family, 'archive').length) {
      items.push({
        key: 'archive',
        label: 'Archive',
        icon: <Archive size={15} aria-hidden="true" />,
        disabled: visibilityLocked,
        onSelect: () => confirmArchiveFamily(family),
      })
    }
    if (canBulkVisibility && familyMemberIds(family, 'restore').length) {
      items.push({
        key: 'restore',
        label: 'Restore',
        icon: <ArchiveRestore size={15} aria-hidden="true" />,
        disabled: visibilityLocked,
        onSelect: () => runFamilyVisibility(family, false),
      })
    }
    // A mixed family (some copies marked from a regulation's queue) undoes
    // its pending standalone copies here; Erase now stays on wholly pending
    // families, since a permanent action must not sit on a live-looking row.
    if (canPublishPosts && familyMemberIds(family, 'undoDelete').length) {
      items.push({
        key: 'undo-delete',
        label: 'Undo deletion',
        icon: <ArchiveRestore size={15} aria-hidden="true" />,
        disabled: listInteractionLocked || undoFamilyDeleteMutation.isPending,
        onSelect: () => confirmUndoFamilyDelete(family),
      })
    }
    if (canPublishPosts && familyIsDeletable(family)) {
      items.push({
        key: 'delete',
        label: 'Delete post',
        icon: <Trash2 size={15} aria-hidden="true" />,
        danger: true,
        disabled: listInteractionLocked || bulkDeleteMutation.isPending,
        onSelect: () => confirmDeleteFamily(family),
      })
    }
    return items
  }

  const queueMenuItems = (item) => {
    const items = []
    const pendingDelete = item.lifecycle === 'pending_delete' || Boolean(item.pending_delete_at)
    if (pendingDelete) {
      // A distributed (grouped) pending row restores through the Command Center
      // group undo so every destination comes back together; that endpoint needs
      // Command Center access, so a non-CC editor on the org dashboard would only
      // get a 403. Offer the undo to a single post (its org-scoped undo works for
      // any editor); a grouped row needs an operator with Command Center access.
      if (isEditor && (!item.post_group_id || commandCenterAccess)) {
        items.push({
          key: 'undo-delete',
          label: 'Undo deletion',
          icon: <ArchiveRestore size={15} aria-hidden="true" />,
          disabled: queueInteractionLocked || undoPendingDeleteMutation.isPending,
          onSelect: () => confirmUndoPendingDelete(item),
        })
      }
      if (!commandCenter && canForceDelete) {
        items.push({
          key: 'force-delete',
          label: 'Erase now',
          icon: <Trash2 size={15} aria-hidden="true" />,
          danger: true,
          disabled: queueInteractionLocked || forceDeleteMutation.isPending,
          onSelect: () => confirmForceDelete(item),
        })
      }
      return items
    }
    if (canEditPosts && !item.is_hidden && canUsePostEditor(item, commandCenterAccess)) {
      items.push({
        key: 'edit',
        label: item.post_group_id ? 'Edit distributed post' : (commandCenter ? 'Edit post' : 'Open full editor'),
        icon: <Pencil size={15} aria-hidden="true" />,
        onSelect: () => void openEditor(item),
      })
    }
    // A member moderation holds or rejected carries its status; Command
    // Center can release it past an outage or a switched-off organization,
    // or revoke an exemption, from the post's full read model. A family
    // spanning books offers one item per member with moderation state.
    if (commandCenter) {
      const members = rowMembers(item)
      const moderated = members.filter((member) => member.moderation && member.organization_slug)
      moderated.forEach((member) => {
        items.push({
          key: `moderation:${member.post_id}`,
          label: moderated.length > 1 || members.length > 1
            ? `Moderation · ${member.organization_name || member.organization_slug}…`
            : 'Moderation…',
          icon: <ShieldAlert size={15} aria-hidden="true" />,
          onSelect: () => setModerationItem({
            id: member.post_id,
            organization_slug: member.organization_slug,
            organization_name: member.organization_name,
            title: item.title,
            text: item.text,
          }),
        })
      })
    }
    if (!commandCenter && canOrderPosts && !item.is_hidden) {
      items.push({
        key: 'placement',
        label: 'Placement',
        icon: <ListOrdered size={15} aria-hidden="true" />,
        onSelect: () => openPlacement(item),
      })
    }
    if (!commandCenter && canBulkVisibility && item.is_hidden && !isGeneratedParlay(item)) {
      items.push({
        key: 'restore',
        label: 'Restore',
        icon: <ArchiveRestore size={15} aria-hidden="true" />,
        disabled: queueInteractionLocked || bulkVisibilityMutation.isPending,
        onSelect: () => bulkVisibilityMutation.mutate({ isHidden: false, postIds: [item.id] }),
      })
    }
    if (!commandCenter && canPublishPosts && item.delete_capability?.allowed) {
      items.push({
        key: 'delete',
        label: 'Delete post',
        icon: <Trash2 size={15} aria-hidden="true" />,
        danger: true,
        onSelect: () => confirmDeletePost(item),
      })
    }
    return items
  }

  // The old always-on "Posts" title row is gone: the summary strip renders
  // only when it carries something an operator can read or act on (scope,
  // order state, page selection, provider hint), so the default Command
  // Center view no longer shows a near-empty header above the table.
  const queueScopeText = isOverview ? 'All regulations' : selectedOfferingLabel
  const showOrderState = canOrderPosts && !isOverview
  // Reordering is withheld rather than shown-and-disabled, so the strip
  // says what is missing instead of leaving the controls unexplained. That
  // includes the no-book case: it is the state the page opens in, so
  // staying silent there reads as "this page cannot reorder" rather than
  // "you have not scoped it yet". The chain stays quiet only before a
  // provider is chosen, where showProviderHint already speaks.
  const commandCenterReorderHint = !commandCenter || !canBulkCommand
    ? ''
    : !organizationId
      ? 'Select an organization to reorder'
      : offeringOptions.length === 0
        ? 'This book has no states to reorder'
        : !offering
          ? 'Select a state to reorder'
          : hasNarrowingFilters
            ? 'Clear filters to reorder'
            : !commandCenterOrderPageIsRankable
              ? 'Reorder from page 1 first'
              : ''
  const reorderHint = isOverview && canOrderPosts && hasNarrowingFilters
    ? 'Clear filters to reorder'
    : commandCenterReorderHint
  const showSelectionControl = commandCenter ? canBulkCommand : canSelectPosts
  const showProviderHint = !isOverview && commandCenter && !canBulkCommand && providers.length > 1
  const showReturnToAutomatic = !isOverview && canOrderPosts && isCanonicalCurrentCreatorScope && hasManualOrder && !isOrderDirty
  const showQueueSummary = Boolean(queueScopeText) || showOrderState || Boolean(reorderHint)
    || showSelectionControl || showProviderHint || showReturnToAutomatic

  const queuePane = (
    <section className={styles.queueColumn} aria-label="Posts">
      {showQueueSummary ? (
        <div className={styles.queueSummary}>
          <div className={styles.queueHeading}>
            {queueScopeText ? <span className={styles.queueScope}>{queueScopeText}</span> : null}
            {showOrderState ? <span className={styles.orderState}>{orderStateLabel}</span> : null}
            {reorderHint ? (
              <span className={styles.orderState}>{reorderHint}</span>
            ) : null}
          </div>
          <div className={styles.queueHeaderActions}>
            {showProviderHint ? (
              <span className={styles.queueScope}>Select a provider to work with posts</span>
            ) : null}
            {showSelectionControl ? (
              isOverview ? (
                <PageSelectionControl
                  loadedCount={overviewItems.length}
                  selectedCount={selectedFamilyKeys.length}
                  allLoadedSelected={allOverviewSelected}
                  mutationPending={listInteractionLocked || bulkVisibilityMutation.isPending || bulkDeleteMutation.isPending}
                  onSelectPage={() => setSelectedFamilyKeys(overviewItems.map((family) => family.family_key))}
                  onClearSelection={clearSelectedFamilies}
                />
              ) : (
                <PageSelectionControl
                  loadedCount={queueDraft.length}
                  selectedCount={selectedPostIds.length}
                  allLoadedSelected={allLoadedSelected}
                  mutationPending={queueInteractionLocked || bulkVisibilityMutation.isPending || bulkDeleteMutation.isPending || bulkCommandMutation.isPending}
                  onSelectPage={() => setSelectedPostIds(queueDraft.map((item) => item.id))}
                  onClearSelection={clearSelectedPosts}
                />
              )
            ) : null}
            {canForceDelete && lifecycle === 'pending_delete' && eraseTargets.length > 0 ? (
              <Button
                variant="danger"
                size="compact"
                onClick={confirmForceDeleteSelected}
                disabled={forceDeleteManyMutation.isPending}
                loading={forceDeleteManyMutation.isPending}
              >
                Erase now ({eraseTargets.length})
              </Button>
            ) : null}
            {showReturnToAutomatic ? (
              <Button
                variant="ghost"
                size="compact"
                onClick={confirmAutomaticOrder}
                disabled={resetSurfaceOrderMutation.isPending || (commandCenter && !organizationId)}
                loading={resetSurfaceOrderMutation.isPending}
              >
                Return to automatic
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      {isOverview ? (
        <div
          ref={queueFocusRef}
          className={styles.queueList}
          role="list"
          aria-label="Content overview"
          tabIndex={-1}
        >
          <PostRowHeader showSelection={canSelectPosts} orderSlots={overviewOrderSlots} />
          {overviewItems.map((family, index) => {
            const rootId = familyRootId(family)
            const representative = family.representative || {}
            const title = representative.title || representative.text || 'Untitled post'
            const reach = familyReach(family)
            // A post moderation holds is not live; the badge says why. A
            // family marked for deletion otherwise shows its erase countdown.
            const moderationBadge = moderationHoldBadge(representative.moderation)
            const statusBadge = moderationBadge || (familyIsPendingDelete(family)
              ? { key: 'pending-delete-countdown', label: pendingDeleteCountdown(familyPendingDeleteExpiresAt(family)), tone: 'warning' }
              : null)
            return (
              <PostRow
                key={family.family_key}
                id={family.family_key}
                rank={listOffset + index + 1}
                index={index}
                total={overviewItems.length}
                media={representative.media?.[0] || null}
                creator={representative.creator?.display_name}
                title={title}
                subtitle={representative.subtitle}
                regulationLabel={reach.label}
                regulationDetail={reach.detail}
                variantCount={family.variant_count || 0}
                selectionCount={representative.selection_count ?? 0}
                createdLabel={formatListDate(representative.created_at)}
                status={statusBadge}
                selected={canSelectPosts ? selectedFamilyKeys.includes(family.family_key) : undefined}
                onSelectedChange={(checked) => setSelectedFamilyKeys((currentValue) => (
                  checked
                    ? [...new Set([...currentValue, family.family_key])]
                    : currentValue.filter((key) => key !== family.family_key)
                ))}
                active={inspectorRowId === rootId}
                draggable={canInteractWithOverviewOrder}
                isDragged={canInteractWithOverviewOrder && overviewDraggedKey === family.family_key}
                onDragStart={canInteractWithOverviewOrder ? setOverviewDraggedKey : undefined}
                onDragOver={canInteractWithOverviewOrder ? () => {} : undefined}
                onDrop={canInteractWithOverviewOrder ? handleOverviewDrop : undefined}
                onDragEnd={canInteractWithOverviewOrder ? () => setOverviewDraggedKey(null) : undefined}
                onMoveToTop={canOrderOverview ? () => moveOverviewFamily(family.family_key, 'top') : undefined}
                onMoveUp={canOrderOverview ? () => moveOverviewFamily(family.family_key, 'up') : undefined}
                onMoveDown={canOrderOverview ? () => moveOverviewFamily(family.family_key, 'down') : undefined}
                orderActionsDisabled={overviewMoveToTopPending}
                orderSlots={overviewOrderSlots}
                onOpen={() => openInspector(rootId)}
                onPreviewEnter={showPreviewFor(representative)}
                onPreviewLeave={hidePreview}
                menuItems={overviewMenuItems(family)}
              />
            )
          })}
        </div>
      ) : (
        <div
          ref={queueFocusRef}
          className={styles.queueList}
          role="list"
          aria-label="Feed operations queue"
          tabIndex={-1}
        >
          <PostRowHeader
            showSelection={commandCenter ? canBulkCommand : canSelectPosts}
            columns={{ variants: false }}
            orderSlots={queueOrderSlots}
          />
          {queueDraft.map((item, index) => {
            const orderable = canOrderPosts
              && !item.is_hidden
              && !isGeneratedQueueItem(item)
              // A Command Center row is a family: it is only orderable
              // when at least one collapsed member can carry a manual
              // rank, since the envelope is built from members.
              && (!commandCenter || rowMembers(item).some(BULK_MEMBER_FILTERS.setOrder))
            const title = item.title || item.text || 'Untitled post'
            // Command Center rows are families: one row per logical post,
            // covering every collapsed member (distributed-post members and
            // regulation forks). Coverage is summarized as books and
            // regulations instead of repeating one member's scope. The
            // per-org dashboard keeps its original single-post label.
            const members = rowMembers(item)
            const memberBookCount = new Set(members.map((member) => member.organization_id)).size
            const coverageRegulations = new Set()
            let coverageUnresolved = false
            members.forEach((member) => {
              const offerings = member.target_offerings?.length
                ? member.target_offerings
                : orgOfferingsById.get(member.organization_id) || []
              if (!offerings.length) {
                coverageUnresolved = true
                return
              }
              offerings.forEach((offeringId) => coverageRegulations.add(offeringId))
            })
            const singleRegulation = coverageRegulations.size === 1 && !coverageUnresolved
              ? [...coverageRegulations][0]
              : null
            const coverageLabel = coverageUnresolved
              ? 'All regulations'
              : singleRegulation
                ? regulationFor(singleRegulation) || singleRegulation
                : coverageRegulations.size > 1
                  ? `${members.length > 1 ? '' : 'Shared · '}${coverageRegulations.size} regulations`
                  : selectedOfferingLabel
            const regulationLabel = commandCenter
              ? coverageLabel
              : (item.target_offerings?.length || 0) > 1
                ? `Shared · ${item.target_offerings.length} regulations`
                : regulationFor(item.target_offerings?.[0] || '')
                  || item.target_offerings?.[0]
                  || selectedOfferingLabel
            const chips = []
            // A post moderation holds is not live; the badge says why. A
            // Command Center family sums its members' states.
            const moderationBadge = commandCenter ? membersModerationBadge(members) : moderationHoldBadge(item.moderation)
            // The server's six-value lifecycle API is the single source of
            // truth for row, inspector, and group labels. Legacy rows without
            // the field still use operator-facing Archived copy.
            if (item.lifecycle && item.lifecycle !== 'active') {
              chips.push({
                key: 'lifecycle',
                label: item.lifecycle_label || 'Archived',
                tone: item.lifecycle === 'archived_settled' ? 'neutral' : 'warning',
              })
            } else if (item.is_hidden) {
              chips.push({ key: 'archived', label: 'Archived', tone: 'warning' })
            }
            if (item.lifecycle === 'pending_delete' && item.pending_delete_at) {
              chips.push({
                key: 'pending-delete-countdown',
                label: pendingDeleteCountdown(item.pending_delete_expires_at),
                tone: 'warning',
              })
            }
            if (item.parlay) {
              chips.push({
                key: 'parlay',
                label: item.parlay.origin === 'generated' ? 'Generated parlay' : 'Parlay',
                tone: 'info',
              })
            }
            // A campaign label names a cross-book artifact, so it only belongs
            // in front of an operator who is allowed to know about the other books.
            if (item.post_group_label && commandCenterAccess) {
              chips.push({ key: 'group', label: item.post_group_label, tone: 'neutral' })
            }
            if (commandCenter && memberBookCount > 1) {
              chips.push({ key: 'org', label: `${memberBookCount} books`, tone: 'neutral' })
            } else if (commandCenter && item.organization_name) {
              chips.push({ key: 'org', label: item.organization_name, tone: 'neutral' })
            }
            const absoluteRank = queueOffset + index + 1
            return (
              <Fragment key={item.id}>
                <PostRow
                  id={item.id}
                  rank={absoluteRank}
                  index={index}
                  total={queueDraft.length}
                  media={item.media_objects?.[0] || item.media?.[0] || null}
                  creator={item.creator_name}
                  title={title}
                  subtitle={item.subtitle}
                  regulationLabel={regulationLabel}
                  regulationDetail={parlayStateLabel(item, selectedOfferingLabel)}
                  variantCount={0}
                  selectionCount={item.parlay?.leg_count ?? 0}
                  createdLabel={formatListDate(item.created_at)}
                  chips={chips}
                  status={moderationBadge}
                  selected={(commandCenter ? canBulkCommand : canSelectPosts) ? selectedPostIds.includes(item.id) : undefined}
                  onSelectedChange={(checked) => setSelectedPostIds((currentValue) => (checked
                    ? [...new Set([...currentValue, item.id])]
                    : currentValue.filter((postId) => postId !== item.id)))}
                  active={inspectorRowId === item.id}
                  draggable={orderable}
                  isDragged={orderable && draggedId === item.id}
                  onDragStart={orderable ? setDraggedId : undefined}
                  onDragOver={orderable ? () => {} : undefined}
                  onDrop={orderable ? handleDrop : undefined}
                  onDragEnd={orderable ? () => setDraggedId(null) : undefined}
                  onMoveToTop={orderable ? () => movePost(item.id, 'top') : undefined}
                  onMoveUp={orderable ? () => movePost(item.id, 'up') : undefined}
                  onMoveDown={orderable ? () => movePost(item.id, 'down') : undefined}
                  // A move made after Save is silently discarded by the
                  // post-save refetch, so the controls freeze while the
                  // set_order command is in flight.
                  orderActionsDisabled={commandCenter && orderSavePending}
                  orderSlots={queueOrderSlots}
                  columns={{ variants: false }}
                  onOpen={() => openInspector(item.id)}
                  onPreviewEnter={showPreviewFor(item)}
                  onPreviewLeave={hidePreview}
                  menuItems={queueMenuItems(item)}
                />
                {/* A real entry in the queue's sequence, not a decoration: it
                    carries the copy that explains why every row under it is
                    dark on the carousel, so it is announced in place rather
                    than being an unowned child of the list. */}
                {showVisibilityCutoff && absoluteRank === surfaceVisibleItemLimit ? (
                  <div className={styles.visibilityCutoff} role="listitem">
                    <span>Not shown in this carousel</span>
                    <span className={styles.visibilityCutoffCount}>
                      {`${surfaceVisibleItemLimit} visible \u00b7 ${visibilityCutoffBelowCount} below limit`}
                    </span>
                  </div>
                ) : null}
              </Fragment>
            )
          })}
        </div>
      )}
      {queuePageCount > 1 ? (
        <div className={styles.queuePagination}>
          <Pagination
            page={Math.min(page, queuePageCount)}
            pageCount={queuePageCount}
            onPageChange={(nextPage) => {
              setPlacementItem(null)
              setInspectorRowId(null)
              setPage(nextPage)
            }}
            label={isOverview ? 'Overview pages' : 'Queue pages'}
          />
        </div>
      ) : null}
      <div className={styles.visuallyHidden} aria-live="polite" aria-atomic="true">{rankAnnouncement}</div>
    </section>
  )


  const queueControls = (
    <QueueControls
      commandCenter={commandCenter}
      archivedOnly={archivedOnly}
      canSelectPosts={canSelectPosts}
      canBulkVisibility={canBulkVisibility}
      canBulkDelete={canBulkDelete}
      providers={providers}
      organizations={organizations}
      providerId={providerId}
      organizationId={organizationId}
      setProviderId={(value) => updateFilter(setProviderId, value)}
      setOrganizationId={(value) => updateFilter(setOrganizationId, value)}
      surface={surface}
      setSurface={(value) => updateFilter(setSurface, value)}
      surfaceOptions={surfaceOptions}
      offering={offering}
      setOffering={(value) => updateFilter(setOffering, value)}
      offeringOptions={offeringOptions}
      scopeLabel={isOverview ? '' : selectedOfferingLabel}
      bookState={bookState}
      setBookState={(value) => updateFilter(setBookState, value)}
      source={source}
      setSource={(value) => updateFilter(setSource, value)}
      sourceOptions={sourceOptions}
      moderationFilter={moderationFilter}
      moderationHeldCount={moderationHeldCount}
      onToggleModerationHeld={toggleModerationHeld}
      includePersonalized={includePersonalized}
      setIncludePersonalized={(value) => updateFilter(setIncludePersonalized, value)}
      status={status}
      setStatus={(value) => updateFilter(setStatus, value)}
      visibility={visibility}
      setVisibility={(value) => updateFilter(setVisibility, value)}
      contentType={contentType}
      setContentType={(value) => updateFilter(setContentType, value)}
      parlayStatus={parlayStatus}
      setParlayStatus={(value) => updateFilter(setParlayStatus, value)}
      parlayOrigin={parlayOrigin}
      setParlayOrigin={(value) => updateFilter(setParlayOrigin, value)}
      creator={searchAndCreator}
      setCreator={(value) => updateFilter(setSearchAndCreator, value)}
      tagId={tagId}
      setTagId={(value) => updateFilter(setTagId, value)}
      availableTags={availableTags}
      resultRange={listInteractionLocked ? (isOverview ? 'Updating overview…' : 'Updating posts…') : resultRange}
      searchLoading={activeQuery.isFetching}
      onRefresh={() => activeQuery.refetch()}
      refreshPending={activeQuery.isFetching}
      activeFilters={activeFilters}
      onRemoveFilter={removeAdvancedFilter}
      onClearAdvancedFilters={clearAdvancedFilters}
      {...(isOverview ? {
        selectedCount: listInteractionLocked ? 0 : selectedFamilyKeys.length,
        selectedVisibleCount: overviewSelection.archive.familyCount,
        selectedArchivedCount: overviewSelection.restore.familyCount,
        deletableSelectedCount: overviewSelection.delete.familyCount,
        protectedSelectedCount: overviewSelection.protectedCount,
        onClearSelection: clearSelectedFamilies,
        onRestoreSelected: () => bulkVisibilityMutation.mutate({
          isHidden: false,
          postIds: overviewSelection.restore.postIds,
          unitCount: overviewSelection.restore.familyCount,
        }),
        onArchiveSelected: confirmArchiveSelectedFamilies,
        onDeleteSelected: confirmDeleteSelectedFamilies,
      } : {
        selectedCount: queueInteractionLocked ? 0 : selectedPostIds.length,
        selectedVisibleCount: selectedVisibleIds.length,
        selectedArchivedCount: restorableSelectedIds.length,
        deletableSelectedCount: deletableSelectedIds.length,
        protectedSelectedCount,
        onClearSelection: clearSelectedPosts,
        onRestoreSelected: () => bulkVisibilityMutation.mutate({ isHidden: false, postIds: restorableSelectedIds }),
        onArchiveSelected: () => bulkVisibilityMutation.mutate({ isHidden: true, postIds: selectedVisibleIds }),
        onDeleteSelected: confirmDeleteSelected,
      })}
      deleteRestrictionText={deleteRestrictionText}
      visibilityActionPending={visibilityActionPending}
      deletePending={bulkDeleteMutation.isPending}
      canBulkCommand={canBulkCommand}
      bulkScopeSummary={bulkScopeSummary}
      bulkGroupedSelectedCount={bulkGroupedSelectedCount}
      bulkArchivableCount={bulkSelection.archive.count}
      bulkRestorableCount={bulkSelection.restore.count}
      bulkMoveToTopEligibleCount={bulkSelection.moveToTop.count}
      bulkDeleteEligibleCount={bulkSelection.deleteDrafts.count}
      bulkPromoteEligibleCount={bulkSelection.promote.count}
      bulkDemoteEligibleCount={bulkSelection.demote.count}
      bulkCommandPending={bulkCommandMutation.isPending}
      bulkCommandPendingKind={bulkCommandPendingKind}
      bulkVisibleItemLimitInput={bulkVisibleItemLimitInput}
      setBulkVisibleItemLimitInput={setBulkVisibleItemLimitInput}
      onBulkArchive={bulkArchiveSelectedCommandCenter}
      onBulkRestore={bulkRestoreSelectedCommandCenter}
      onBulkMoveToTop={bulkMoveToTopSelectedCommandCenter}
      onBulkDeleteDrafts={bulkDeleteSelectedCommandCenter}
      onBulkPromote={bulkPromoteSelectedCommandCenter}
      onBulkDemote={bulkDemoteSelectedCommandCenter}
      onBulkSetVisibleItemLimit={bulkSetVisibleItemLimitCommandCenter}
    />
  )

  const overviewState = overviewQuery.isError && !overviewItems.length ? (
    <StatePanel state="error" title="Overview could not load" description="Your filters are preserved." onRetry={() => overviewQuery.refetch()} />
  ) : overviewQuery.isLoading ? (
    <StatePanel state="loading" title="Loading overview" description="Posts that say the same thing are grouped together." />
  ) : !overviewItems.length && hasNarrowingFilters ? (
    <StatePanel
      state="search-empty"
      title="No content matches these filters"
      description="Clear the search or filters to see more content."
      onClear={clearNarrowingFilters}
    />
  ) : !overviewItems.length ? (
    <StatePanel
      state="empty"
      title="No posts yet"
      description={canPublishPosts ? 'Create a post to start this queue.' : 'No posts have been added to this surface.'}
    />
  ) : null

  const scopedQueueState = queueQuery.isError && !queueContextMatches ? (
    <StatePanel state="error" title="Queue could not load" description="Your filters are preserved." onRetry={() => queueQuery.refetch()} />
  ) : queueQuery.isLoading || !queueContextMatches ? (
    <StatePanel state="loading" title="Loading queue" description="Posts will appear in ranked order." />
  ) : queueQuery.isError && !queueDraft.length ? (
    <StatePanel state="error" title="Queue could not load" description="Your filters are preserved." onRetry={() => queueQuery.refetch()} />
  ) : !queueDraft.length && hasNarrowingFilters ? (
    <StatePanel
      state="search-empty"
      title="No posts match these filters"
      description="Clear the search or filters to see more posts."
      onClear={clearNarrowingFilters}
    />
  ) : !queueDraft.length ? (
    <StatePanel
      state="empty"
      title="No posts yet"
      description={canPublishPosts && !commandCenter
        ? 'Create a post to start this queue.'
        : 'No posts have been added to this surface.'}
    />
  ) : null

  const queueState = isOverview ? overviewState : scopedQueueState

  const offeringNotice = offeringWarning ? (
    <Alert variant="warning" className={styles.offeringWarning}>{offeringWarning}</Alert>
  ) : null

  // Central Manage Posts' primary lifecycle control (Active/Archived/All) and
  // the default-off personalized-bet toggle. Browsing stays read-only across
  // every lifecycle tab; only the provider-scoped selection/bulk flow gates
  // on a single provider.
  const lifecycleBar = commandCenter ? (
    <div className={styles.lifecycleBar}>
      <SegmentedControl
        label="Post lifecycle"
        value={lifecycle}
        onValueChange={(value) => updateFilter(setLifecycle, value)}
        items={LIFECYCLE_OPTIONS.map((option) => ({
          value: option.value,
          label: lifecycleCounts ? `${option.label} (${lifecycleCounts[option.value] ?? 0})` : option.label,
        }))}
      />
    </div>
  ) : null

  const queueWorkspacePane = (
    <div className={styles.queueWorkspace}>
      {offeringNotice}
      {lifecycleBar}
      {queueControls}
      {commandCenter ? (
        <BulkCommandResultPanel
          result={bulkCommandResult}
          organizations={organizations}
          regulationLabelById={regulationLabelById}
          onDismiss={dismissBulkCommandResult}
        />
      ) : null}
      {activeQuery.isError && listLength > 0 ? (
        <StatePanel
          compact
          state="error"
          title={isOverview ? 'Overview could not refresh' : 'Posts could not refresh'}
          description="The previous results are still shown."
          onRetry={() => activeQuery.refetch()}
        />
      ) : null}
      {queuePane}
    </div>
  )

  const queueStatePane = (
    <div className={styles.queueWorkspace}>
      {offeringNotice}
      {lifecycleBar}
      {queueControls}
      {commandCenter ? (
        <BulkCommandResultPanel
          result={bulkCommandResult}
          organizations={organizations}
          regulationLabelById={regulationLabelById}
          onDismiss={dismissBulkCommandResult}
        />
      ) : null}
      {queueState}
    </div>
  )


  const postInspector = (
    <PostInspectorDrawer
      open={Boolean(inspectorData)}
      family={inspectorData}
      branding={commandCenter ? inspectorBrandingQuery.data : brandingQuery.data}
      regulationFor={regulationFor}
      // Grouped members are read-only here on every mount: the ordinary post
      // update endpoint rejects them, so the only edit path is the group editor.
      // On the org mount the inspector saves through the feed-ops routes, which
      // need Feed configure or operate; publish-only editors use the full editor.
      canEdit={(commandCenter ? canEditPosts : canOperatePosts) && !inspectorPostIsGrouped}
      readOnlyNote={!commandCenter && canPublishPosts && !canOperatePosts
        ? 'Visibility, boost and quick edits here need Feed configure access. Use Open full editor to change this post.'
        : ''}
      canChangeRegulations={commandCenter || canOperatePosts}
      saving={savePostMutation.isPending}
      canApproveGenerated={canApproveGenerated}
      publishingGenerated={generatedPublishMutation.isPending}
      publishGeneratedError={generatedPublishMutation.isError
        ? generatedPublishMutation.error?.response?.data?.error || 'Generated parlay could not be published'
        : ''}
      canDeleteGenerated={!commandCenter && canPublishPosts && Boolean(inspectorData?.representative?.delete_capability?.allowed)}
      deletingGenerated={deletePostMutation.isPending}
      onClose={closeInspector}
      scope={inspectorScope}
      scopeOptions={inspectorScopeOptions}
      editorPostId={inspectorScopePostId}
      scopeLoading={inspectorDetailQuery.isLoading}
      detailError={inspectorDetailQuery.isError}
      onRetryDetail={() => inspectorDetailQuery.refetch()}
      onTicketColorsChanged={() => inspectorDetailQuery.refetch()}
      editorLabel={inspectorPostIsGrouped ? 'Edit distributed post' : (commandCenter ? 'Edit post' : 'Open full editor')}
      editorAvailable={!inspectorPostIsGrouped || commandCenterAccess}
      groupDestinations={commandCenter ? inspectorGroupQuery.data?.destinations || null : null}
      groupLoading={commandCenter && Boolean(inspectorGroupId) && inspectorGroupQuery.isLoading}
      groupError={commandCenter && Boolean(inspectorGroupId) && inspectorGroupQuery.isError}
      onScopeChange={setInspectorScope}
      onSave={(patch) => savePostMutation.mutate({
        family: inspectorData,
        patch,
        scope: inspectorScope,
      })}
      onRegulationChange={(variant, changes) => regulationSettingsMutation.mutate({ variant, changes })}
      onResetVariant={(variant) => resetVariantMutation.mutate(variant)}
      onOpenFullEditor={() => {
        const item = inspectorData?.representative
        if (item) void openEditor(item)
      }}
      onApproveGenerated={(postId) => generatedPublishMutation.mutate(postId)}
      onDeleteGenerated={confirmDeletePost}
      inline={inlineInspector}
    />
  )

  return (
    <PageLayout
      size="workspace"
      stickyActions={isOverview ? (canOrderOverview && isOverviewOrderDirty ? (
        <div className={styles.stickyOrderStack}>
          {showOverviewReorderWarning ? (
            <Alert variant="warning" className={styles.stickyOrderWarning}>{OVERVIEW_REORDER_WARNING}</Alert>
          ) : null}
          <div className={styles.stickyOrderBar} role="region" aria-label="Unsaved feed order">
            <div>
              <strong>Unsaved order</strong>
              <span>{`Positions ${overviewOrderOffset + 1}\u2013${overviewOrderOffset + overviewOrderFamilies.length} · ${surfaceLabel}`}</span>
            </div>
            <div className={styles.stickyOrderActions}>
              <Button
                variant="secondary"
                onClick={() => setOverviewDraft([])}
                disabled={saveOverviewOrderMutation.isPending || overviewMoveToTopPending}
              >
                Discard changes
              </Button>
              <Button
                aria-label="Save order"
                onClick={saveOverviewOrder}
                disabled={overviewMoveToTopPending}
                loading={saveOverviewOrderMutation.isPending}
              >
                <Save size={16} aria-hidden="true" />
                Save order
              </Button>
            </div>
          </div>
        </div>
      ) : null) : !queueInteractionLocked && canOrderPosts && queueDraft.length > 0 && isOrderDirty ? (
        <div className={styles.stickyOrderBar} role="region" aria-label="Unsaved queue order">
          <div>
            <strong>Unsaved order</strong>
            <span>{queueDraft.length} posts · {commandCenter && commandCenterOrderBookName ? `${commandCenterOrderBookName} · ` : ''}{surfaceLabel}{selectedOfferingLabel ? ` · ${selectedOfferingLabel}` : ''}</span>
          </div>
          <div className={styles.stickyOrderActions}>
            {commandCenter && offeringOptions.length > 1 ? (
              <label className={styles.stickyOrderOption}>
                <input
                  type="checkbox"
                  checked={applyOrderToAllStates}
                  onChange={(event) => setApplyOrderToAllStates(event.target.checked)}
                />
                Apply to all states
              </label>
            ) : null}
            <Button
              variant="secondary"
              onClick={discardCurrentOrder}
              disabled={orderSavePending}
            >
              Discard changes
            </Button>
            <Button
              aria-label="Save order"
              onClick={saveCurrentOrder}
              disabled={!queueDraft.length}
              loading={orderSavePending}
            >
              <Save size={16} aria-hidden="true" />
              Save order
            </Button>
          </div>
        </div>
      ) : null}
    >
      <PageHeader
        title={headerTitle}
        mode="workspace"
        context={headerContext}
        subtitle={commandCenter ? 'Operate organization queues without losing provider or destination context.' : undefined}
        actions={canPreviewSurface || (!commandCenter && canPublishPosts) ? (
          <div className={styles.actionRow}>
            {canPreviewSurface ? (
              <LinkButton
                href={`/dashboard/feed/preview?surface=${encodeURIComponent(surface)}`}
                variant="secondary"
              >
                <MonitorPlay size={16} aria-hidden="true" />
                Preview surface
              </LinkButton>
            ) : null}
            {!commandCenter && canPublishPosts ? (
              <LinkButton href="/dashboard/feed/posts/scheduling" variant="secondary">
                Scheduling
              </LinkButton>
            ) : null}
            {!commandCenter && canPublishPosts ? (
              <Button onClick={() => navigate('/dashboard/feed/posts/new')}>
                <Plus size={16} aria-hidden="true" />
                Create post
              </Button>
            ) : null}
          </div>
        ) : null}
      />
      {archivedOnly ? <MediaSectionTabs /> : null}

      <div className={`${styles.postsWorkspace} ${inlineInspector && inspectorData ? styles.postsWorkspaceWithInspector : ''}`.trim()}>
        <div className={styles.postsWorkspacePrimary}>
          {queueState ? queueStatePane : queueWorkspacePane}
        </div>
        {postInspector}
      </div>

      <Dialog
        open={Boolean(placementItem)}
        title="Post placement"
        description={placementItem
          ? `${placementItem.title || placementItem.text || 'Untitled post'} · ${selectedOfferingLabel || 'Default queue'}`
          : undefined}
        onClose={() => setPlacementItem(null)}
      >
        {placementForm}
      </Dialog>


      <PostHoverPreview
        anchor={hoverPreview.anchor}
        branding={brandingQuery.data}
        item={hoverPreviewItem}
      />

      {commandCenter && moderationItem ? (
        <ModerationOverrideDialog
          key={`${moderationItem.organization_slug}:${moderationItem.id}`}
          orgSlug={moderationItem.organization_slug}
          postId={moderationItem.id}
          postTitle={moderationItem.title || moderationItem.text || 'Untitled post'}
          organizationName={moderationItem.organization_name}
          onClose={() => setModerationItem(null)}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(confirmAction)}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmLabel={confirmAction?.confirmLabel || 'Confirm'}
        variant={confirmAction?.variant || 'default'}
        onConfirm={confirmAction?.onConfirm || (() => {})}
        onCancel={() => setConfirmAction(null)}
      />
    </PageLayout>
  )
}

export default FeedOpsPage
